import React, { useEffect, useMemo, useRef, useState } from "react";

// KickStart! – Klassens aktivitetsverktøy (uten Tailwind CSS)

// -----------------------------
// HJELPEFUNKSJONER & HOOKS
// -----------------------------
function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function beep(duration = 500, freq = 880) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.frequency.value = freq;
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    g.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + duration / 1000);
    setTimeout(() => {
      o.stop();
      ctx.close();
    }, duration);
  } catch (e) {
    // no-op if audio blocked
  }
}

// Erstatter localStorage med React state + localStorage backup
function useAppStorage(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const item = window.localStorage.getItem(key);
        return item ? JSON.parse(item) : initialValue;
      }
    } catch (error) {
      console.warn('localStorage ikke tilgjengelig:', error);
    }
    return initialValue;
  });

  const setStoredValue = (newValue) => {
    try {
      setValue(newValue);
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(key, JSON.stringify(newValue));
      }
    } catch (error) {
      console.warn('Kunne ikke lagre til localStorage:', error);
    }
  };

  return [value, setStoredValue];
}

// -----------------------------
// GLOBAL APP-STATE (Context)
// -----------------------------
const defaultState = {
  classes: [
    { 
      id: "klasseliste-1", 
      name: "klasse", 
      students: ["Ola", "Kari", "Lise", "Lars"],
      teams: []
    },
  ],
  activeClassId: "klasseliste-1",
  scoreboard: [
    { id: "t1", name: "Lag 1", score: 0 },
    { id: "t2", name: "Lag 2", score: 0 }
  ]
};

const AppStateContext = React.createContext(null);
const AppStateDispatchContext = React.createContext(null);

function AppStateProvider({ children }) {
  const [state, setState] = useAppStorage("kickstart_state", defaultState);
  return (
    <AppStateContext.Provider value={state}>
      <AppStateDispatchContext.Provider value={setState}>
        {children}
      </AppStateDispatchContext.Provider>
    </AppStateContext.Provider>
  );
}

function useAppState() {
  const s = React.useContext(AppStateContext);
  const d = React.useContext(AppStateDispatchContext);
  if (!s || !d) {
    throw new Error("AppStateProvider mangler. Pakk App i <AppStateProvider>.");
  }
  return [s, d];
}

// -----------------------------
// HOVEDAPP
// -----------------------------
export default function App() {
  return (
    <AppStateProvider>
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          
          @keyframes ping {
            75%, 100% {
              transform: scale(2);
              opacity: 0;
            }
          }
          
          ::-webkit-scrollbar {
            width: 8px;
          }
          
          ::-webkit-scrollbar-track {
            background: rgba(255, 255, 255, 0.1);
            border-radius: 4px;
          }
          
          ::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.3);
            border-radius: 4px;
          }
          
          ::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.5);
          }
          
          select option {
            background: #1f2937 !important;
            color: white !important;
          }
        `
      }} />
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #334155 0%, #475569 50%, #64748b 100%)',
        color: 'white',
        display: 'flex',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <Sidebar />
        <MainContent />
      </div>
    </AppStateProvider>
  );
}

// -----------------------------
// SIDEBAR
// -----------------------------
function Sidebar() {
  return (
    <div style={{
      width: '384px',
      background: 'rgba(0, 0, 0, 0.2)',
      backdropFilter: 'blur(20px)',
      borderRight: '1px solid rgba(255, 255, 255, 0.1)',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Header />
      <div style={{
        flex: 1,
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        overflowY: 'auto'
      }}>
        <ClassPanel />
        <RandomStudent />
        <Timers />
        <Scoreboard />
        <FullscreenToggle />
      </div>
    </div>
  );
}

// --- Aktivitet: 5 på 30 sekunder
const FIVE_IN_30_CATEGORIES = [
  "Land i Europa", "YouTubere", "Ting man kan gjøre på ferie", "Norske byer",
  "Dyr som lever i havet", "Ting i et klasserom", "Matvarer som er røde", "Ting som kunne skjedd på en helt vanlig mandag", "Yrker", "Bilmerker", "Sosiale medier", "Videospill", "Filmer", "Matvarer du aldri ville hatt på pizza", 
  "Netflix-serier", "Ting man spiser til frokost", "Instrumenter", "Land i Afrika", "Norske politikere", 
  "Land i Asia", "Fotballklubber", "Ting som er grønne", "Kjente personer", "Dyr du helst ikke vil møte i en skog", 
  "Ting man gjør på skolen", "Dyr i Afrika", "Ting i kjøleskapet", "Nynorske verb", "Ting som alltid blir ødelagt på skolen", "Ting lærere sier ofte", "Ting som kan få deg til å le", "Forskjellige fugler", "Superkrefter", 
  "Mobil-apper", "Språk", "Norske artister", "Planeter", "Ting som flyr", "Ting du ville hatt med på en øde øy", 
  "Klær", "Ting man gjør på sommeren", "Skolefag", "Ting som er kalde", "Måter å si «hei» på", 
  "Ting man kan samle på", "Land i Amerika", "TV-kanaler", "Ting på et bibliotek"
];

function FiveIn30() {
  const [showTask, setShowTask] = useState(false);
  const [category, setCategory] = useState("");
  const [usedCategories, setUsedCategories] = useState([]);
  const [key, setKey] = useState(0);

  function startActivity() {
    const availableCategories = usedCategories.length >= FIVE_IN_30_CATEGORIES.length 
      ? FIVE_IN_30_CATEGORIES 
      : FIVE_IN_30_CATEGORIES.filter(cat => !usedCategories.includes(cat));
    
    const newCategory = availableCategories[randInt(0, availableCategories.length - 1)];
    setCategory(newCategory);
    setShowTask(true);
    setKey((k) => k + 1);
    
    if (usedCategories.length >= FIVE_IN_30_CATEGORIES.length) {
      setUsedCategories([newCategory]);
    } else {
      setUsedCategories([...usedCategories, newCategory]);
    }
  }

  const buttonStyle = {
    padding: '16px 32px',
    borderRadius: '24px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '20px',
    background: 'linear-gradient(45deg, #16a34a, #166534)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease'
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        ⏱️ 5 på 30 sekunder
      </div>
      <p style={{
        color: '#cbd5e1',
        marginBottom: '32px',
        fontSize: '18px'
      }}>Ramse opp 5 ting innen kategorien på 30 sekunder!</p>
      
      {!showTask ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <p style={{ fontSize: '20px', color: '#cbd5e1' }}>Klar for utfordringen?</p>
          <button style={buttonStyle} onClick={startActivity}>
            🚀 Vis kategori
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <div style={{
            fontSize: '48px',
            fontWeight: 'bold',
            padding: '48px 32px',
            borderRadius: '24px',
            background: 'linear-gradient(45deg, rgba(22, 163, 74, 0.2), rgba(22, 101, 52, 0.2))',
            border: '1px solid rgba(22, 163, 74, 0.3)',
            textAlign: 'center'
          }}>
            🎯 <span style={{
              background: 'linear-gradient(45deg, #fcd34d, #fdba74)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>5 {category}</span>
          </div>
          
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{ fontSize: '16px', color: '#cbd5e1' }}>
		<div style={{ marginBottom: '8px' }}>💡 Trekk en tilfeldig elev</div>
              <div style={{ marginBottom: '8px' }}>⏱️ Eleven skal ramse opp 5 ting som passer i løpet av 30 sekunder</div>
             </div>
          </div>

          <div style={{
            display: 'flex',
            gap: '24px',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SmallCountdown key={key} seconds={30} autoStart={true} />
            <button 
              style={{
                ...buttonStyle,
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                fontSize: '16px',
                padding: '12px 24px'
              }}
              onClick={startActivity}
            >
              🔄 Ny kategori
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Aktivitet: Emoji-ansikt
const EMOJI_FACES = [
  "😀", "😁", "😂", "🤣", "😊", "😇", "🙂", "🙃", "😉", "😍", "🥰", "😘", 
  "😗", "☺️", "😚", "😙", "😋", "😛", "😝", "😜", "🤪", "🤨", "🧐", "🤓", 
  "😎", "🤩", "🥳", "😏", "😒", "😞", "😔", "😟", "😕", "🙁", "☹️", "😣", 
  "😖", "😫", "😩", "🥺", "😢", "😭", "😤", "😠", "😡", "🤬", "🤯", "😳", 
  "🥵", "🥶", "😱", "😨", "😰", "😥", "😓", "🤗", "🤔", "🤭", "🤫", "🤥", 
  "😶", "😐", "😑", "😬", "🙄", "😯", "😦", "😧", "😮", "😲", "🥱", "😴", 
  "🤤", "😪", "😵", "🤐", "🥴", "🤢", "🤮", "🤧", "😷", "🤒", "🤕"
];

function EmojiFace() {
  const [showTask, setShowTask] = useState(false);
  const [emoji, setEmoji] = useState("");
  const [usedEmojis, setUsedEmojis] = useState([]);
  const [showingDuration, setShowingDuration] = useState(false);

  function startActivity() {
    const availableEmojis = usedEmojis.length >= EMOJI_FACES.length 
      ? EMOJI_FACES 
      : EMOJI_FACES.filter(em => !usedEmojis.includes(em));
    
    const newEmoji = availableEmojis[randInt(0, availableEmojis.length - 1)];
    setEmoji(newEmoji);
    setShowTask(true);
    setShowingDuration(true);
    
    // Vis emoji i 5 sekunder
    setTimeout(() => {
      setShowingDuration(false);
    }, 5000);
    
    if (usedEmojis.length >= EMOJI_FACES.length) {
      setUsedEmojis([newEmoji]);
    } else {
      setUsedEmojis([...usedEmojis, newEmoji]);
    }
  }

  const buttonStyle = {
    padding: '16px 32px',
    borderRadius: '24px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '20px',
    background: 'linear-gradient(45deg, #f59e0b, #d97706)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease'
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        😄 Emoji-ansikt
      </div>
      <p style={{
        color: '#cbd5e1',
        marginBottom: '32px',
        fontSize: '18px'
      }}>Vis ansiktet som emojien viser i 5 sekunder!</p>
      
      {!showTask ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <p style={{ fontSize: '20px', color: '#cbd5e1' }}>Klar til å vise ansiktet?</p>
          <button style={buttonStyle} onClick={startActivity}>
            🎭 Vis emoji
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <div style={{
            fontSize: '200px',
            padding: '48px',
            borderRadius: '24px',
            background: showingDuration 
              ? 'linear-gradient(45deg, rgba(245, 158, 11, 0.3), rgba(217, 119, 6, 0.3))'
              : 'rgba(255, 255, 255, 0.1)',
            border: showingDuration 
              ? '2px solid rgba(245, 158, 11, 0.5)'
              : '1px solid rgba(255, 255, 255, 0.2)',
            textAlign: 'center',
            transition: 'all 0.3s ease',
            animation: showingDuration ? 'pulse 1s infinite' : 'none'
          }}>
            {emoji}
          </div>
          
          {showingDuration ? (
            <div style={{
              background: 'linear-gradient(45deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.2))',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '16px',
              padding: '24px',
              fontSize: '24px',
              fontWeight: 'bold'
            }}>
              🎭 Vis dette ansiktet nå!
            </div>
          ) : (
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '24px',
              fontSize: '18px'
            }}>
              ✅ Tiden er ute! Hvem viste ansiktet best?
            </div>
          )}

          <div style={{
            display: 'flex',
            gap: '24px',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <button 
              style={{
                ...buttonStyle,
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                fontSize: '16px',
                padding: '12px 24px'
              }}
              onClick={startActivity}
            >
              🔄 Nytt ansikt
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Header() {
  return (
    <header style={{
      padding: '16px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px',
          borderRadius: '16px',
          background: 'linear-gradient(45deg, #fbbf24, #f97316)',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '18px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }}>
          ⚡
        </div>
        <div>
          <h1 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #fcd34d, #fdba74)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            margin: 0
          }}>
            KickStart!
          </h1>
          <p style={{
            fontSize: '12px',
            color: '#cbd5e1',
            margin: 0
          }}>Klassens aktivitetsverktøy</p>
        </div>
      </div>
    </header>
  );
}

// -----------------------------
// HOVEDINNHOLD
// -----------------------------
function MainContent() {
  return (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column'
    }}>
      <div style={{
        padding: '24px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        background: 'rgba(0, 0, 0, 0.1)',
        textAlign: 'center'
      }}>
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 4px 0' }}>🎯 Aktiviteter</h2>
        <p style={{ fontSize: '14px', color: '#cbd5e1', margin: 0 }}>Velg en aktivitet fra listen under</p>
      </div>
      
      <div style={{
        flex: 1, // Denne er viktig for at området skal fylle plassen
        padding: '24px'
      }}>
        <ActivityHub />
      </div>
    </div>
  );
}

// -----------------------------
// GLASS CARD COMPONENT
// -----------------------------
function GlassCard({ children, gradient = false }) {
  const baseStyle = {
    backdropFilter: 'blur(20px)',
    background: gradient 
      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.15), rgba(255, 255, 255, 0.05))'
      : 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease',
    padding: '16px'
  };

  return <div style={baseStyle}>{children}</div>;
}

// -----------------------------
// KLASSE- OG ELEVHÅNDTERING
// -----------------------------
function ClassPanel() {
  const [state, setState] = useAppState();
  const { classes, activeClassId } = state;
  const active = classes.find((c) => c.id === activeClassId) || classes[0];
  const [newClassName, setNewClassName] = useState("");
  const [studentName, setStudentName] = useState("");
  const [showTeams, setShowTeams] = useState(false);

  function setActive(id) {
    setState({ ...state, activeClassId: id });
  }
  
  function addClass() {
    if (!newClassName.trim()) return;
    const id = `class-${Date.now()}`;
    setState({
      ...state,
      classes: [...classes, { id, name: newClassName.trim(), students: [], teams: [] }],
      activeClassId: id,
    });
    setNewClassName("");
  }

  function deleteClass(classId) {
    if (classes.length <= 1) {
      alert("Du må ha minst én klasse!");
      return;
    }
    
    const classToDelete = classes.find(c => c.id === classId);
    if (!window.confirm(`Vil du slette klassen "${classToDelete?.name}"?`)) return;
    
    const newClasses = classes.filter(c => c.id !== classId);
    const newActiveId = classId === activeClassId ? newClasses[0].id : activeClassId;
    
    setState({
      ...state,
      classes: newClasses,
      activeClassId: newActiveId
    });
  }
  
  function addStudent() {
    if (!studentName.trim()) return;
    const s = studentName.trim();
    if (active.students.includes(s)) return;
    updateClass(active.id, { students: [...active.students, s] });
    setStudentName("");
  }
  
  function removeStudent(s) {
    updateClass(active.id, { students: active.students.filter((x) => x !== s) });
  }
  
  function updateClass(id, patch) {
    setState({
      ...state,
      classes: classes.map((c) => (c.id === id ? { ...c, ...patch } : c)),
    });
  }

  function createTeams() {
    if (!active?.students?.length) return;
    const numTeams = parseInt(prompt("Hvor mange lag? (2-6)", "2")) || 2;
    if (numTeams < 2 || numTeams > 6) return;
    
    const shuffled = shuffle([...active.students]);
    const teams = Array.from({ length: numTeams }, (_, i) => ({
      id: `team-${i+1}`,
      name: `Lag ${i+1}`,
      members: []
    }));
    
    shuffled.forEach((student, index) => {
      teams[index % numTeams].members.push(student);
    });
    
    updateClass(active.id, { teams });
    setShowTeams(true);
  }

  function clearTeams() {
    updateClass(active.id, { teams: [] });
    setShowTeams(false);
  }

  function clearAllStudents() {
    if (!window.confirm(`Vil du slette alle elever fra ${active?.name}?`)) return;
    updateClass(active.id, { students: [], teams: [] });
    setShowTeams(false);
  }

  function exportClass() {
    if (!active) return;
    const dataStr = JSON.stringify({
      name: active.name,
      students: active.students,
      teams: active.teams,
      exportDate: new Date().toISOString()
    }, null, 2);
    
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${active.name.replace(/[^a-zA-Z0-9]/g, '_')}_elevliste.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function importClass() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target.result);
          if (!imported.name || !Array.isArray(imported.students)) {
            alert('Ugyldig filformat!');
            return;
          }
          
          const newId = `imported-${Date.now()}`;
          const newClass = {
            id: newId,
            name: imported.name,
            students: imported.students || [],
            teams: imported.teams || []
          };
          
          setState({
            ...state,
            classes: [...classes, newClass],
            activeClassId: newId
          });
          
          alert(`Importerte klasse "${imported.name}" med ${imported.students.length} elever!`);
        } catch (error) {
          alert('Kunne ikke lese filen. Sjekk at det er en gyldig JSON-fil.');
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }

  const inputStyle = {
    borderRadius: '12px',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    padding: '8px 12px',
    color: 'white',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.3s ease'
  };

  const buttonStyle = {
    padding: '8px 12px',
    borderRadius: '12px',
    fontWeight: '600',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '14px'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.3)'
  };

  const dangerButtonStyle = {
    ...buttonStyle,
    background: 'linear-gradient(45deg, #dc2626, #b91c1c)'
  };

  return (
    <GlassCard gradient>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        <h3 style={{
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          margin: 0
        }}>
          👥 Elevlister
        </h3>
        <div style={{
          fontSize: '12px',
          color: '#cbd5e1',
          padding: '4px 8px',
          borderRadius: '9999px',
          background: 'rgba(100, 116, 139, 0.2)'
        }}>
          💾 Lokal
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            style={{ 
              ...inputStyle, 
              flex: 1,
              backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' fill=\'none\' viewBox=\'0 0 20 20\'%3e%3cpath stroke=\'%236b7280\' stroke-linecap=\'round\' stroke-linejoin=\'round\' stroke-width=\'1.5\' d=\'m6 8 4 4 4-4\'/%3e%3c/svg%3e")',
              backgroundPosition: 'right 8px center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '16px',
              paddingRight: '32px'
            }}
            value={active?.id || ""}
            onChange={(e) => setActive(e.target.value)}
          >
            {classes.map((c) => (
              <option key={c.id} value={c.id} style={{ background: '#1f2937', color: 'white' }}>{c.name}</option>
            ))}
          </select>
          {classes.length > 1 && (
            <button 
              style={dangerButtonStyle}
              onClick={() => deleteClass(active?.id)}
              title="Slett klasse"
            >
              🗑️
            </button>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            style={{ ...inputStyle, flex: 1 }}
            placeholder="Ny elev…"
            value={studentName}
            onChange={(e) => setStudentName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addStudent()}
          />
          <button style={{
            ...buttonStyle,
            background: 'linear-gradient(45deg, #059669, #047857)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }} onClick={addStudent}>+</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', fontSize: '12px' }}>
            <button 
              style={secondaryButtonStyle}
              onClick={() => setShowTeams(!showTeams)}
            >
              {showTeams ? "👥 Vis elever" : "🏃‍♀️ Vis lag"}
            </button>
            <button style={secondaryButtonStyle} onClick={createTeams}>
              🎲 Del i lag
            </button>
            {active?.teams?.length > 0 && (
              <button style={dangerButtonStyle} onClick={clearTeams}>
                🗑️ Lag
              </button>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '8px', fontSize: '12px' }}>
            <button style={secondaryButtonStyle} onClick={exportClass}>
              📤 Eksporter
            </button>
            <button style={secondaryButtonStyle} onClick={importClass}>
              📥 Importer
            </button>
            {active?.students?.length > 0 && (
              <button style={dangerButtonStyle} onClick={clearAllStudents}>
                🗑️ Alle elever
              </button>
            )}
          </div>
        </div>

        <div style={{ 
          maxHeight: '192px', 
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          {!showTeams ? (
            // Vis individuelle elever
            active?.students?.map((s) => (
              <div key={s} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '8px 12px',
                transition: 'all 0.3s ease',
                fontSize: '14px',
                position: 'relative'
              }}>
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: '500'
                }} title={s}>{s}</span>
                <button 
                  style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '4px',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => removeStudent(s)}
                  onMouseOver={(e) => e.target.style.color = '#ef4444'}
                  onMouseOut={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.6)'}
                >
                  ✕
                </button>
              </div>
            ))
          ) : (
            // Vis lag
            active?.teams?.map((team) => (
              <div key={team.id} style={{
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '12px'
              }}>
                <div style={{
                  fontWeight: 'bold',
                  fontSize: '14px',
                  marginBottom: '8px',
                  color: '#fbbf24'
                }}>{team.name}</div>
                <div style={{
                  fontSize: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px'
                }}>
                  {team.members.map(member => (
                    <div key={member} style={{ color: '#cbd5e1' }}>{member}</div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div style={{ 
          paddingTop: '8px', 
          borderTop: '1px solid rgba(255, 255, 255, 0.2)' 
        }}>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              placeholder="Ny klasse…"
              value={newClassName}
              onChange={(e) => setNewClassName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addClass()}
            />
            <button 
              style={{
                ...buttonStyle,
                background: 'linear-gradient(45deg, #059669, #047857)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
              }} 
              onClick={addClass}
            >+</button>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

// -----------------------------
// TILFELDIG ELEV
// -----------------------------
function RandomStudent() {
  const [state] = useAppState();
  const active = state.classes.find((c) => c.id === state.activeClassId) || state.classes[0];
  const [spinning, setSpinning] = useState(false);
  const [lastPick, setLastPick] = useState("");
  const [pool, setPool] = useState([]);
  const [count, setCount] = useState(1);
  const [pickMode, setPickMode] = useState("student");

  useEffect(() => {
    setLastPick("");
    setPool([]);
  }, [state.activeClassId]);

  const remaining = useMemo(() => {
    const source = pickMode === "team" ? (active?.teams || []).map(t => t.name) : (active?.students || []);
    const set = new Set(pool);
    return source.filter((s) => !set.has(s));
  }, [active, pool, pickMode]);

  function spin() {
    const source = pickMode === "team" ? (active?.teams || []).map(t => t.name) : (active?.students || []);
    if (!source.length) return;
    
    const candidateList = count > 1 ? multiPick() : singlePick();
    animatePick(candidateList, source);
  }

  function singlePick() {
    const source = pickMode === "team" ? (active?.teams || []).map(t => t.name) : (active?.students || []);
    const availableSource = remaining.length ? remaining : source;
    const pick = availableSource[randInt(0, availableSource.length - 1)];
    const newPool = remaining.length ? [...pool, pick] : [pick];
    setPool(newPool);
    setLastPick(pick);
    return [pick];
  }

  function multiPick() {
    const source = pickMode === "team" ? (active?.teams || []).map(t => t.name) : (active?.students || []);
    const availableSource = remaining.length ? remaining : source;
    const n = Math.min(count, availableSource.length);
    const picks = shuffle(availableSource).slice(0, n);
    const newPool = remaining.length ? [...pool, ...picks] : [...picks];
    setPool(newPool);
    setLastPick(picks.join(", "));
    return picks;
  }

  function resetCycle() {
    setPool([]);
    setLastPick("");
  }

  function animatePick(picks, source) {
    if (spinning) return;
    setSpinning(true);
    const display = document.getElementById("picker-display");
    if (!display) return;
    const names = shuffle(source);
    let i = 0;
    const timer = setInterval(() => {
      display.textContent = names[i % names.length];
      i++;
    }, 60);
    setTimeout(() => {
      clearInterval(timer);
      display.textContent = picks.join(", ");
      setSpinning(false);
      beep(300, 1200);
    }, 1500);
  }

  const sourceCount = pickMode === "team" ? (active?.teams || []).length : (active?.students || []).length;

  const buttonStyle = {
    padding: '8px 12px',
    borderRadius: '12px',
    fontWeight: '600',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '12px'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.3)'
  };

  return (
    <GlassCard gradient>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        <h3 style={{
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          margin: 0
        }}>
          🎲 Tilfeldig trekk
        </h3>
        <button 
          style={{
            ...secondaryButtonStyle,
            textDecoration: 'underline'
          }}
          onClick={resetCycle}
        >
          🔄 Ny runde
        </button>
      </div>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <div style={{ display: 'flex', gap: '8px', fontSize: '12px' }}>
          <button 
            style={{
              ...buttonStyle,
              background: pickMode === "student" ? 'linear-gradient(45deg, #059669, #047857)' : 'rgba(255, 255, 255, 0.1)',
              border: pickMode === "student" ? 'none' : '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: pickMode === "student" ? '0 4px 12px rgba(0, 0, 0, 0.3)' : 'none'
            }}
            onClick={() => {setPickMode("student"); resetCycle();}}
          >
            👤 Elever
          </button>
          <button 
            style={{
              ...buttonStyle,
              background: pickMode === "team" ? 'linear-gradient(45deg, #059669, #047857)' : 'rgba(255, 255, 255, 0.1)',
              border: pickMode === "team" ? 'none' : '1px solid rgba(255, 255, 255, 0.3)',
              opacity: !active?.teams?.length ? 0.6 : 1,
              cursor: !active?.teams?.length ? 'not-allowed' : 'pointer',
              boxShadow: pickMode === "team" ? '0 4px 12px rgba(0, 0, 0, 0.3)' : 'none'
            }}
            onClick={() => {setPickMode("team"); resetCycle();}}
            disabled={!active?.teams?.length}
          >
            🏃‍♀️ Lag ({active?.teams?.length || 0})
          </button>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', fontWeight: '500' }}>Antall:</span>
          <input 
            type="number" 
            min={1} 
            max={5} 
            value={count} 
            onChange={(e) => setCount(parseInt(e.target.value || 1))} 
            style={{
              width: '48px',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              padding: '4px 8px',
              color: 'white',
              fontSize: '12px',
              outline: 'none'
            }}
          />
          <button 
            style={{
              ...buttonStyle,
              flex: 1,
              background: spinning ? '#6b7280' : 'linear-gradient(45deg, #059669, #047857)',
              cursor: spinning ? 'not-allowed' : 'pointer',
              opacity: spinning ? 0.6 : 1,
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
            }}
            onClick={spin} 
            disabled={spinning}
          >
            {spinning ? "🎲 Trekker..." : "🎯 Trekk"}
          </button>
        </div>
        
        <div 
          id="picker-display" 
          style={{
            height: '64px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: 'bold',
            letterSpacing: '0.05em',
            textAlign: 'center',
            padding: '8px',
            transition: 'all 0.3s ease',
            background: spinning 
              ? 'linear-gradient(45deg, #fbbf24, #f97316)' 
              : 'linear-gradient(45deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
            animation: spinning ? 'pulse 1s infinite' : 'none',
            boxShadow: spinning ? '0 4px 16px rgba(251, 191, 36, 0.4)' : 'none'
          }}
        >
          {lastPick || "—"}
        </div>
        
        <div style={{ fontSize: '12px', color: '#cbd5e1' }}>
          📊 Gjenstående: <span style={{ fontWeight: 'bold', color: '#fbbf24' }}>{remaining.length}/{sourceCount}</span>
          {remaining.length === 0 && sourceCount > 0 && (
            <div style={{ color: '#22c55e', marginTop: '4px' }}>✅ Alle er trekt! Trykk "Ny runde"</div>
          )}
        </div>
      </div>
    </GlassCard>
  );
}

// -----------------------------
// TIDTAKERE
// -----------------------------
function Timers() {
  return (
    <GlassCard gradient>
      <h3 style={{
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        margin: '0 0 16px 0'
      }}>
        ⏱️ Tidtakere
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Stopwatch />
        <Countdown />
      </div>
    </GlassCard>
  );
}

function Stopwatch() {
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(null);

  useEffect(() => {
    let t;
    if (running) {
      startRef.current = Date.now() - elapsed;
      t = setInterval(() => setElapsed(Date.now() - startRef.current), 50);
    }
    return () => clearInterval(t);
  }, [running, elapsed]);

  function reset() {
    setRunning(false);
    setElapsed(0);
  }

  function formatTime(ms) {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    const millis = Math.floor((ms % 1000) / 10);
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(2, "0")}`;
  }

  const buttonStyle = {
    padding: '4px 8px',
    borderRadius: '8px',
    fontWeight: '600',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '12px'
  };

  return (
    <div style={{
      borderRadius: '12px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      background: 'rgba(255, 255, 255, 0.05)',
      padding: '12px'
    }}>
      <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>⏱️ Stoppeklokke</div>
      <div style={{
        fontFamily: 'Courier New, monospace',
        fontSize: '24px',
        textAlign: 'center',
        color: '#fbbf24',
        fontWeight: 'bold',
        marginBottom: '12px'
      }}>
        {formatTime(elapsed)}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <button 
          style={{
            ...buttonStyle,
            flex: 1,
            background: running 
              ? 'linear-gradient(45deg, #6b7280, #4b5563)' 
              : 'linear-gradient(45deg, #059669, #047857)'
          }}
          onClick={() => setRunning((r) => !r)}
        >
          {running ? "⏸️ Pause" : "▶️ Start"}
        </button>
        <button 
          style={{
            ...buttonStyle,
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}
          onClick={reset}
        >
          🔄
        </button>
      </div>
    </div>
  );
}

function Countdown() {
  const [seconds, setSeconds] = useState(60);
  const [remaining, setRemaining] = useState(60);
  const [running, setRunning] = useState(false);
  const endAtRef = useRef(null);

  useEffect(() => { setRemaining(seconds); }, [seconds]);
  useEffect(() => {
    let t;
    if (running) {
      endAtRef.current = Date.now() + remaining * 1000;
      t = setInterval(() => {
        const ms = endAtRef.current - Date.now();
        if (ms <= 0) {
          setRunning(false);
          setRemaining(0);
          clearInterval(t);
          beep(600, 1000);
        } else {
          setRemaining(Math.ceil(ms / 1000));
        }
      }, 200);
    }
    return () => clearInterval(t);
  }, [running, remaining]);

  function start() {
    if (remaining <= 0) setRemaining(seconds);
    setRunning(true);
  }

  function reset() {
    setRunning(false);
    setRemaining(seconds);
  }

  const buttonStyle = {
    padding: '4px 8px',
    borderRadius: '8px',
    fontWeight: '600',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '12px'
  };

  return (
    <div style={{
      borderRadius: '12px',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      background: 'rgba(255, 255, 255, 0.05)',
      padding: '12px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '8px'
      }}>
        <div style={{ fontSize: '14px', fontWeight: '600' }}>⏰ Nedtelling</div>
        <div style={{ display: 'flex', gap: '4px', fontSize: '12px' }}>
          {[30, 60, 120].map((s) => (
            <button 
              key={s} 
              style={{
                ...buttonStyle,
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
              onClick={() => { setSeconds(s); setRemaining(s); }}
            >
              {s < 60 ? `${s}s` : `${s/60}m`}
            </button>
          ))}
        </div>
      </div>
      <div style={{
        fontFamily: 'Courier New, monospace',
        fontSize: '24px',
        textAlign: 'center',
        fontWeight: 'bold',
        marginBottom: '12px',
        color: remaining <= 10 && running ? '#ef4444' : '#fbbf24',
        animation: remaining <= 10 && running ? 'pulse 1s infinite' : 'none'
      }}>
        {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, "0")}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <input 
          type="number" 
          style={{
            width: '48px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            padding: '4px 8px',
            color: 'white',
            fontSize: '12px',
            outline: 'none'
          }}
          value={seconds} 
          onChange={(e) => { const v = parseInt(e.target.value || 0); setSeconds(v); setRemaining(v); }} 
        />
        <button 
          style={{
            ...buttonStyle,
            flex: 1,
            background: 'linear-gradient(45deg, #059669, #047857)'
          }}
          onClick={running ? () => setRunning(false) : start}
        >
          {running ? "⏸️ Pause" : "▶️ Start"}
        </button>
        <button 
          style={{
            ...buttonStyle,
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}
          onClick={reset}
        >
          🔄
        </button>
      </div>
    </div>
  );
}

// -----------------------------
// POENGTAVLE
// -----------------------------
function Scoreboard() {
  const [state, setState] = useAppState();
  const [name, setName] = useState("");

  function addTeam() {
    if (!name.trim()) return;
    const newTeam = { id: `t-${Date.now()}`, name: name.trim(), score: 0 };
    setState({ ...state, scoreboard: [...state.scoreboard, newTeam] });
    setName("");
  }
  
  function removeTeam(id) {
    setState({ ...state, scoreboard: state.scoreboard.filter((t) => t.id !== id) });
  }
  
  function changeScore(id, delta) {
    setState({
      ...state,
      scoreboard: state.scoreboard.map((t) => (t.id === id ? { ...t, score: Math.max(-999, t.score + delta) } : t))
    });
  }
  
  function resetAll() {
    if (!window.confirm("Nullstille alle poeng?")) return;
    setState({
      ...state,
      scoreboard: state.scoreboard.map((t) => ({ ...t, score: 0 }))
    });
  }

  const buttonStyle = {
    padding: '8px 12px',
    borderRadius: '12px',
    fontWeight: '600',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '14px'
  };

  const scoreButtonStyle = {
    padding: '4px 8px',
    borderRadius: '8px',
    fontWeight: '600',
    fontSize: '12px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease'
  };

  return (
    <GlassCard gradient>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '12px'
      }}>
        <h3 style={{
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          margin: 0
        }}>
          🏆 Poengtavle
        </h3>
        <button 
          style={{
            ...buttonStyle,
            fontSize: '12px',
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.3)'
          }}
          onClick={resetAll}
        >
          🔄
        </button>
      </div>
      
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '12px', 
        marginBottom: '12px' 
      }}>
        {state.scoreboard.map((t) => (
          <div key={t.id} style={{
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            background: 'rgba(255, 255, 255, 0.05)',
            padding: '12px',
            position: 'relative'
          }}>
            <button 
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                color: 'rgba(255, 255, 255, 0.4)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '12px',
                transition: 'all 0.3s ease'
              }}
              onClick={() => removeTeam(t.id)}
              onMouseOver={(e) => e.target.style.color = '#ef4444'}
              onMouseOut={(e) => e.target.style.color = 'rgba(255, 255, 255, 0.4)'}
            >
              ✕
            </button>
            <div style={{
              fontWeight: 'bold',
              fontSize: '14px',
              color: '#fbbf24',
              marginBottom: '8px'
            }}>{t.name}</div>
            <div style={{
              fontFamily: 'Courier New, monospace',
              fontSize: '24px',
              textAlign: 'center',
              color: '#fbbf24',
              fontWeight: 'bold',
              marginBottom: '8px'
            }}>
              {t.score}
            </div>
            <div style={{
              display: 'flex',
              gap: '4px',
              justifyContent: 'center'
            }}>
              <button 
                style={{
                  ...scoreButtonStyle,
                  background: 'rgba(239, 68, 68, 0.2)',
                  color: '#ef4444'
                }}
                onClick={() => changeScore(t.id, -1)}
              >
                -1
              </button>
              <button 
                style={{
                  ...scoreButtonStyle,
                  background: 'rgba(34, 197, 94, 0.2)',
                  color: '#22c55e'
                }}
                onClick={() => changeScore(t.id, +1)}
              >
                +1
              </button>
              <button 
                style={{
                  ...scoreButtonStyle,
                  background: 'rgba(59, 130, 246, 0.2)',
                  color: '#3b82f6'
                }}
                onClick={() => changeScore(t.id, +5)}
              >
                +5
              </button>
            </div>
          </div>
        ))}
      </div>
      
      <div style={{ display: 'flex', gap: '8px' }}>
        <input 
          style={{
            flex: 1,
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            padding: '8px 12px',
            color: 'white',
            fontSize: '14px',
            outline: 'none'
          }}
          placeholder="Nytt lag…" 
          value={name} 
          onChange={(e) => setName(e.target.value)} 
          onKeyDown={(e) => e.key === "Enter" && addTeam()} 
        />
        <button 
          style={{
            ...buttonStyle,
            background: 'linear-gradient(45deg, #64748b, #475569)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }}
          onClick={addTeam}
        >
          +
        </button>
      </div>
    </GlassCard>
  );
}

// -----------------------------
// FULLSKJERM
// -----------------------------
function FullscreenToggle() {
  const [isFs, setIsFs] = useState(!!document.fullscreenElement);
  useEffect(() => {
    function onChange() { setIsFs(!!document.fullscreenElement); }
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggle = async () => {
    if (!document.fullscreenElement) await document.documentElement.requestFullscreen();
    else await document.exitFullscreen();
  };
  return (
    <GlassCard gradient>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <div style={{
            fontWeight: 'bold',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            🖥️ Presentasjon
          </div>
          <div style={{
            fontSize: '12px',
            color: '#cbd5e1',
            marginTop: '4px'
          }}>
            Fullskjerm for prosjektor
          </div>
        </div>
        <button 
          style={{
            padding: '8px 12px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            background: 'rgba(255, 255, 255, 0.1)',
            color: 'white',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '12px',
            transition: 'all 0.3s ease'
          }}
          onClick={toggle}
        >
          {isFs ? "🔽" : "🔼"}
        </button>
      </div>
    </GlassCard>
  );
}

// -----------------------------
// AKTIVITETER
// -----------------------------
const ACTIVITIES = [
  { id: "find", name: "Klar–ferdig–finn!", icon: "🔍", colors: ["#059669", "#047857"] },
  { id: "emoji", name: "Emoji-historie", icon: "😀", colors: ["#d97706", "#f59e0b"] },
  { id: "ordjakt", name: "Ordjakt", icon: "📝", colors: ["#2563eb", "#1d4ed8"] },
  { id: "tallkamp", name: "Tallkamp", icon: "🔢", colors: ["#7c3aed", "#6d28d9"] },
  { id: "hvem", name: "Hvem er jeg?", icon: "❓", colors: ["#0891b2", "#0e7490"] },
  { id: "quiz", name: "Quiz", icon: "🧠", colors: ["#475569", "#334155"] },
  { id: "wheel", name: "Lykkehjul", icon: "🎡", colors: ["#dc2626", "#b91c1c"] },
  { id: "dilemma", name: "Dilemma", icon: "⚖️", colors: ["#16a34a", "#15803d"] },
  { id: "sixplaces", name: "6-plasser", icon: "🎲", colors: ["#ea580c", "#c2410c"] },
  { id: "hangman", name: "Hangman", icon: "💀", colors: ["#7c2d12", "#92400e"] },
  { id: "fourlights", name: "Fire lys", icon: "💡", colors: ["#eab308", "#ca8a04"] },
  { id: "fivein30", name: "5 på 30 sekunder", icon: "⏱️", colors: ["#16a34a", "#166534"] },
  { id: "emojiface", name: "Emoji-ansikt", icon: "😄", colors: ["#f59e0b", "#d97706"] },
  { id: "timeline", name: "Tidslinje", icon: "📅", colors: ["#8b5cf6", "#7c3aed"] },
  { id: "factmyth", name: "Fakta eller myte", icon: "🤔", colors: ["#0891b2", "#0c4a6e"] },
  { id: "jaellernei", name: "Ja eller nei?", icon: "✅", colors: ["#4CAF50", "#2e7d32"], external: "https://ja-nei.ak-kreativ.no" },
  { id: "gjettbildet", name: "Gjett bildet", icon: "🖼️", colors: ["#f97316", "#ea580c"], external: "https://gjett-bildet.ak-kreativ.no" },
  { id: "slange", name: "Slange", icon: "🐍", colors: ["#22c55e", "#16a34a"], external: "https://slange.ak-kreativ.no" },
];

function ActivityHub() {
  const [activeId, setActiveId] = useState(null);
  const [randomKey, setRandomKey] = useState(0);

  useEffect(() => {
    if (activeId) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // 👇 LEGG TIL DENNE TRACKING-KODEN
      if (window.gtag) {
        window.gtag('event', 'aktivitet_start', {
          'aktivitet_navn': activeId
        });
      }
    }
  }, [activeId]);

  function randomActivity() {
    const id = ACTIVITIES[randInt(0, ACTIVITIES.length - 1)].id;
    setActiveId(id);
    setRandomKey((k) => k + 1);
    
    // 👇 LEGG TIL DENNE TRACKING-KODEN
    if (window.gtag) {
      window.gtag('event', 'tilfeldig_aktivitet_klikk', {
        'aktivitet_navn': id
      });
    }
  }

  const buttonStyle = {
    padding: '12px 24px',
    borderRadius: '16px',
    fontWeight: '600',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '16px',
    background: 'linear-gradient(45deg, #64748b, #475569)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
  };

  return (
    <div style={{ height: '100%' }}>
      {!activeId ? (
        <div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button style={buttonStyle} onClick={randomActivity}>
                🎲 Tilfeldig aktivitet
              </button>
            </div>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {ACTIVITIES.map((a) => (
              <div
                key={a.id}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '24px',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  background: 'rgba(255, 255, 255, 0.05)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  transform: 'scale(1)'
                }}
                onClick={() => a.external ? window.open(a.external, '_blank') : setActiveId(a.id)}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 16px 32px rgba(0, 0, 0, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  position: 'absolute',
                  inset: '0',
                  background: `linear-gradient(45deg, ${a.colors[0]}, ${a.colors[1]})`,
                  opacity: 0.1,
                  transition: 'opacity 0.3s ease'
                }}></div>
                
                <div style={{
                  position: 'relative',
                  padding: '32px'
                }}>
                  <div style={{
                    fontSize: '48px',
                    marginBottom: '16px',
                    textAlign: 'center'
                  }}>{a.icon}</div>
                  <div style={{
                    fontWeight: 'bold',
                    fontSize: '20px',
                    marginBottom: '12px',
                    textAlign: 'center'
                  }}>{a.name}</div>
                  <p style={{
                    fontSize: '14px',
                    color: '#cbd5e1',
                    marginBottom: '24px',
                    lineHeight: '1.6',
                    textAlign: 'center'
                  }}>{activityDescription(a.id)}</p>
                  <button style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '16px',
                    fontWeight: 'bold',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    background: `linear-gradient(45deg, ${a.colors[0]}, ${a.colors[1]})`,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                    transition: 'all 0.3s ease'
                  }}>
                    {a.external ? '🔗 Åpne aktivitet' : '🚀 Start aktivitet'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div style={{ height: '100%' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '24px'
          }}>
            <button 
              style={{
                ...buttonStyle,
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
              onClick={() => setActiveId(null)}
            >
              ← Tilbake til aktiviteter
            </button>
            <button style={buttonStyle} onClick={randomActivity}>
              🎲 Tilfeldig ny
            </button>
          </div>

          <GlassCard gradient>
                          {activeId === "find" && <FindIt key={randomKey} />}
              {activeId === "emoji" && <EmojiStory key={randomKey} />}
              {activeId === "ordjakt" && <WordHunt key={randomKey} />}
              {activeId === "tallkamp" && <NumberBattle key={randomKey} />}
              {activeId === "hvem" && <WhoAmI key={randomKey} />}
              {activeId === "quiz" && <QuickQuiz key={randomKey} />}
              {activeId === "wheel" && <WheelOfNames key={randomKey} />}
              {activeId === "dilemma" && <Dilemma key={randomKey} />}
              {activeId === "sixplaces" && <SixPlaces key={randomKey} />}
              {activeId === "hangman" && <Hangman key={randomKey} />}
              {activeId === "fourlights" && <FourLights key={randomKey} />}
              {activeId === "fivein30" && <FiveIn30 key={randomKey} />}
              {activeId === "emojiface" && <EmojiFace key={randomKey} />}
             {activeId === "timeline" && (
    <div style={{ display: 'block', width: '100%' }}>
      <Timeline key={randomKey} />
    </div>
  )}

  {activeId === "factmyth" && <FactMyth key={randomKey} />}
</GlassCard>

        </div>
      )}
    </div>
  );
}

function activityDescription(id) {
  switch (id) {
    case "find":
      return "Finn ting som matcher oppdraget – første elev/lag som viser noe som passer, får poeng.";
    case "emoji":
      return "Få tilfeldige emojis og lag en setning eller mini-historie med dem.";
    case "ordjakt":
      return "Få en bokstav og 60 sekunder – finn flest ord som begynner på den!";
    case "tallkamp":
      return "Kom nærmest måltallet ved å kombinere tall – rask tenking vinner.";
    case "hvem":
      return "Gjett personen/tingen ut fra gradvis tydeligere hint.";
    case "quiz":
      return "Kjappe spørsmål med mulighet for å vise fasit.";
    case "wheel":
      return "Lykkehjul som trekker elev(er) fra aktiv klasse på en morsom måte.";
    case "dilemma":
      return "Velg mellom to alternativer – gå til høyre eller venstre side av klasserommet!";
    case "sixplaces":
      return "Plasser deg på tall 1-6 i klasserommet. Terningen bestemmer hvem som er ute!";
    case "hangman":
      return "Klassisk ordgjetting – en elev bestemmer ordet, resten gjetter bokstaver.";
    case "fourlights":
      return "Fire fargefelt. Et lys tennes etter tilfeldig delay. De tregeste er ute.";
    case "fivein30":
      return "Ramse opp 5 ting innen en kategori på 30 sekunder – hvem får til det?";
    case "emojiface":
      return "Vis emoji-ansiktet som kommer på skjermen – hold det i noen sekunder!";
    // Lim inn de to nye her:
    case "timeline":
      return "Sorter historiske hendelser i riktig kronologisk rekkefølge!";
    case "factmyth":
      return "Er påstanden du ser sann eller usann? Test kunnskapen din!";
    case "jaellernei":
      return "Alle reiser seg og svarer JA eller NEI. Feil svar = sett deg. Siste elev står igjen!";
    case "gjettbildet":
      return "Et bilde avsløres gradvis – hvem gjetter hva det er først?";
    case "slange":
      return "Bygg en ordkjede sammen! Neste ord må begynne med siste bokstav i forrige ord.";
    default:
      return "";
  }
}

// --- AKTIVITETER MED FULL FUNKSJONALITET ---

// --- Aktivitet: Klar–ferdig–finn!
const FIND_PROMPTS = [
  "noe blått", "noe som lager lyd", "et par sokker", "noe som lukter godt",
  "en bok med bilder", "noe som starter på S", "noe mykt", "noe rundt",
  "noe som kan bære vann", "noe du kan skrive med", "noe laget av tre",
  "noe eldre enn deg", "noe med hjul", "noe som kan brettes",
  "noe rødt", "noe som kan åpnes", "noe kaldt", "noe varmt",
  "noe som glitrer", "noe du kan spise", "noe som starter på M",
  "noe firkantet", "noe smalt", "noe gult", "noe gjennomsiktig",
  "noe med tekst", "noe elektronisk", "noe naturlig", "noe kunstig",
  "noe som kan bevege seg", "noe som er fast", "noe som kan brettes",
  "noe som har hull", "noe som er glatt", "noe som er rufsete",
  "noe grønt", "noe gult", "noe fiolett", "noe blått", "noe hvitt",
  "noe som kan henge", "noe du kan tegne på", "noe som lukter vondt",
  "noe du kan kle på deg", "noe som blinker", "noe som har tall",
  "noe du kan lese", "noe som har knapper", "noe du kan kaste",
  "noe som er hardt", "noe som kan rulle", "noe som er søtt",
  "noe som kan knuses", "noe som kan strekkes", "noe som lager skygge",
  "noe med et dyr på", "noe som lager musikk", "noe du kan åpne en dør med",
  "noe som kan åpnes og lukkes", "noe som er veldig lite", "noe som er veldig stort",
  "noe grønt", "noe gult", "noe fiolett", "noe svart", "noe hvitt"
];

function FindIt() {
  const [showTask, setShowTask] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [usedPrompts, setUsedPrompts] = useState([]);

  function startActivity() {
    const availablePrompts = usedPrompts.length >= FIND_PROMPTS.length
      ? FIND_PROMPTS
      : FIND_PROMPTS.filter(p => !usedPrompts.includes(p));

    const newPrompt = availablePrompts[randInt(0, availablePrompts.length - 1)];
    setPrompt(newPrompt);
    setShowTask(true);

    if (usedPrompts.length >= FIND_PROMPTS.length) {
      setUsedPrompts([newPrompt]);
    } else {
      setUsedPrompts([...usedPrompts, newPrompt]);
    }
  }

  const buttonStyle = {
    padding: '16px 32px',
    borderRadius: '24px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '20px',
    background: 'linear-gradient(45deg, #059669, #047857)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease'
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        🔍 Klar–ferdig–finn!
      </div>
      <p style={{
        color: '#cbd5e1',
        marginBottom: '32px',
        fontSize: '18px'
      }}>Vis første ting som matcher oppdraget.</p>

      {!showTask ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <p style={{ fontSize: '20px', color: '#cbd5e1' }}>Klar til å starte?</p>
          <button style={buttonStyle} onClick={startActivity}>
            🚀 Vis oppgave
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <div style={{
            fontSize: '48px',
            fontWeight: 'bold',
            padding: '32px',
            borderRadius: '24px',
            background: 'linear-gradient(45deg, rgba(34, 197, 94, 0.2), rgba(22, 163, 74, 0.2))',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            textAlign: 'center'
          }}>
            🎯 <span style={{
              background: 'linear-gradient(45deg, #fcd34d, #fdba74)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>{prompt}</span>
          </div>
          <div style={{
            display: 'flex',
            gap: '24px',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <button
              style={{
                ...buttonStyle,
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                fontSize: '16px',
                padding: '12px 24px'
              }}
              onClick={startActivity}
            >
              🔄 Nytt oppdrag
            </button>
            <SmallCountdown seconds={30} />
          </div>
        </div>
      )}
    </div>
  );
}

// --- Aktivitet: Emoji-historie
const EMOJIS = [
  '😀', '😅', '😂', '🤣', '😊', '😍', '🤔', '😴', '😎', '😭', '🥳', '🤯', '😬', '😇', '🙃', '🤓', '😡', '👻', '🤖', '🐶', 
  '🐱', '🐸', '🐻', '🦊', '🐼', '🐨', '🐯', '🐧', '🐥', '🦄', '🐝', '🌸', '🍀', '🍎', '🍌', '🍕', '🍔', '🍟', '🍰', '🍫', 
  '🎂', '🍩', '🥐', '☕', '🍵', '🥤', '⚽', '🏀', '🏈', '🎲', '🎯', '🎵', '🎧', '🎸', '🎻', '🎹', '🚲', '🛴', '🚗', '✈️', 
  '🚀', '🏖️', '🏔️', '🌧️', '🌈', '🔥', '⭐', '💡', '⏰', '🎁', '📚', '✏️', '🧩', '🧠', '🌙', '☀️', '❄️', '🌺', '🌻', '🌷', 
  '🦋', '🐠', '🐙', '🐢', '🦉', '🐰', '🐭', '🍓', '🍇', '🍊', '🥕', '🥦', '🍞', '🧀', '🎪', '🎨', '🏠', '🌍', '⛵', '🎯', 
  '🎮', '🎭', '📱', '💻', '🔑', '🎒', '👑', '💎', '🌟', '🏆', '🎸', '🥁', '📷', '🔬', '🛸', '🚂', '🎠', '🍭', '🎈'
];

function EmojiStory() {
  const [showTask, setShowTask] = useState(false);
  const [items, setItems] = useState([]);

  function startActivity(count = 3) {
    setItems(shuffle(EMOJIS).slice(0, count));
    setShowTask(true);
  }

  const buttonStyle = {
    padding: '12px 24px',
    borderRadius: '16px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        😀 Emoji-historie
      </div>
      <p style={{
        color: '#cbd5e1',
        marginBottom: '32px',
        fontSize: '18px'
      }}>Lag en setning eller mini-historie som passer til emojiene.</p>
      
      {!showTask ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <p style={{ fontSize: '20px', color: '#cbd5e1' }}>Hvor mange emojis vil du ha?</p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <button 
              style={{
                ...buttonStyle,
                background: 'linear-gradient(45deg, #d97706, #f59e0b)'
              }}
              onClick={() => startActivity(3)}
            >
              🎲 3 emojis
            </button>
            <button 
              style={{
                ...buttonStyle,
                background: 'linear-gradient(45deg, #dc2626, #b91c1c)'
              }}
              onClick={() => startActivity(5)}
            >
              🎰 5 emojis
            </button>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <div style={{
            fontSize: '64px',
            padding: '32px',
            borderRadius: '24px',
            background: 'linear-gradient(45deg, rgba(251, 191, 36, 0.2), rgba(249, 115, 22, 0.2))',
            border: '1px solid rgba(251, 191, 36, 0.3)'
          }}>
            {items.join(" ")}
          </div>
          <div style={{ display: 'flex', gap: '24px', justifyContent: 'center' }}>
            <button 
              style={{
                ...buttonStyle,
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
              onClick={() => startActivity(3)}
            >
              🎲 Ny trio
            </button>
            <button 
              style={{
                ...buttonStyle,
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
              onClick={() => startActivity(5)}
            >
              🎰 5 emojis
            </button>
            <SmallCountdown seconds={45} />
          </div>
        </div>
      )}
    </div>
  );
}

// --- Aktivitet: Ordjakt
function WordHunt() {
  const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZÆØÅ".split("");
  const [showTask, setShowTask] = useState(false);
  const [letter, setLetter] = useState("");
  const [key, setKey] = useState(0);

  function startActivity() {
    setLetter(LETTERS[randInt(0, LETTERS.length - 1)]);
    setShowTask(true);
    setKey((k) => k + 1);
  }

  const buttonStyle = {
    padding: '16px 32px',
    borderRadius: '24px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '20px',
    background: 'linear-gradient(45deg, #2563eb, #1d4ed8)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease'
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        📝 Ordjakt
      </div>
      <p style={{
        color: '#cbd5e1',
        marginBottom: '32px',
        fontSize: '18px'
      }}>Finn flest ord som begynner på bokstaven. 60 sekunder!</p>
      
      {!showTask ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <p style={{ fontSize: '20px', color: '#cbd5e1' }}>Klar til å få din bokstav?</p>
          <button style={buttonStyle} onClick={startActivity}>
            🎯 Vis bokstav
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <div style={{
            fontSize: '128px',
            fontWeight: 'bold',
            padding: '32px',
            borderRadius: '24px',
            background: 'linear-gradient(45deg, rgba(37, 99, 235, 0.2), rgba(79, 70, 229, 0.2))',
            border: '1px solid rgba(37, 99, 235, 0.3)',
            color: 'white',
            textShadow: '0 4px 8px rgba(0, 0, 0, 0.5)'
          }}>
            {letter}
          </div>
          <div style={{
            display: 'flex',
            gap: '24px',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SmallCountdown key={key} seconds={60} />
            <button 
              style={{
                ...buttonStyle,
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                fontSize: '16px',
                padding: '12px 24px'
              }}
              onClick={startActivity}
            >
              🔄 Ny bokstav
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Liten nedtelling komponent
function SmallCountdown({ seconds = 30, autoStart = false }) {
  const [s, setS] = useState(seconds);
  const [run, setRun] = useState(autoStart);
  
  useEffect(() => { setS(seconds); if (autoStart) setRun(true); }, [seconds, autoStart]);
  useEffect(() => {
    let t;
    if (run && s > 0) {
      t = setInterval(() => setS((x) => x - 1), 1000);
    }
    if (s === 0) {
      beep(500, 900);
      setRun(false);
    }
    return () => clearInterval(t);
  }, [run, s]);
  
  const buttonStyle = {
    padding: '8px 12px',
    borderRadius: '16px',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    background: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s ease'
  };
  
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      padding: '8px 16px'
    }}>
      <div style={{
        fontFamily: 'Courier New, monospace',
        fontSize: '24px',
        minWidth: '3ch',
        textAlign: 'center',
        fontWeight: 'bold',
        color: s <= 10 && run ? '#ef4444' : '#fbbf24',
        transition: 'all 0.3s ease',
        animation: s <= 10 && run ? 'pulse 1s infinite' : 'none'
      }}>
        {s}
      </div>
      {!run ? (
        <button style={buttonStyle} onClick={() => setRun(true)}>
          ▶️ Start
        </button>
      ) : (
        <button style={buttonStyle} onClick={() => setRun(false)}>
          ⏸️ Pause
        </button>
      )}
      <button 
        style={buttonStyle}
        onClick={() => { setS(seconds); setRun(autoStart); }}
      >
        🔄
      </button>
    </div>
  );
}

// --- Aktivitet: Tallkamp
function NumberBattle() {
  const [showTask, setShowTask] = useState(false);
  const [targetNumber, setTargetNumber] = useState(0);
  const [availableNumbers, setAvailableNumbers] = useState([]);
  const [key, setKey] = useState(0);

  function generatePuzzle() {
    const target = randInt(25, 99);
    const count = randInt(5, 6);
    const numbers = [];
    
    for (let i = 0; i < Math.floor(count/2); i++) {
      numbers.push(randInt(1, 10));
    }
    
    for (let i = Math.floor(count/2); i < count; i++) {
      numbers.push(randInt(10, 25));
    }
    
    const shuffledNumbers = shuffle(numbers);
    return { target, numbers: shuffledNumbers };
  }

  function startActivity() {
    const puzzle = generatePuzzle();
    setTargetNumber(puzzle.target);
    setAvailableNumbers(puzzle.numbers);
    setShowTask(true);
    setKey((k) => k + 1);
  }

  const buttonStyle = {
    padding: '16px 32px',
    borderRadius: '24px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '20px',
    background: 'linear-gradient(45deg, #7c3aed, #6d28d9)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease'
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        🔢 Tallkamp
      </div>
      <p style={{
        color: '#cbd5e1',
        marginBottom: '32px',
        fontSize: '18px'
      }}>Bruk tallene nedenfor til å komme så nær måltallet som mulig!</p>
      
      {!showTask ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <p style={{ fontSize: '20px', color: '#cbd5e1' }}>Klar for en matematisk utfordring?</p>
          <button style={buttonStyle} onClick={startActivity}>
            🎯 Vis utfordring
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <div style={{ marginBottom: '32px' }}>
            <div style={{ fontSize: '18px', color: '#cbd5e1', marginBottom: '8px' }}>Måltall:</div>
            <div style={{
              fontSize: '128px',
              fontWeight: 'bold',
              padding: '24px',
              borderRadius: '24px',
              background: 'linear-gradient(45deg, rgba(124, 58, 237, 0.2), rgba(109, 40, 217, 0.2))',
              border: '1px solid rgba(124, 58, 237, 0.3)',
              color: 'white',
              textShadow: '0 4px 8px rgba(0, 0, 0, 0.5)'
            }}>
              {targetNumber}
            </div>
          </div>

          <div style={{ marginBottom: '32px' }}>
            <div style={{ fontSize: '18px', color: '#cbd5e1', marginBottom: '16px' }}>Bruk disse tallene:</div>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {availableNumbers.map((num, index) => (
                <div 
                  key={index}
                  style={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '16px',
                    background: 'linear-gradient(45deg, #475569, #334155)',
                    border: '1px solid #64748b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
                  }}
                >
                  {num}
                </div>
              ))}
            </div>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{ fontSize: '16px', color: '#cbd5e1' }}>
              <div style={{ marginBottom: '8px' }}>✅ Bruk +, -, ×, ÷ til å kombinere tallene</div>
              <div style={{ marginBottom: '8px' }}>✅ Du kan bruke hvert tall kun én gang</div>
              <div style={{ marginBottom: '8px' }}>✅ Du trenger ikke bruke alle tallene</div>
              <div>🏆 Eleven som kommer nærmest vinner!</div>
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: '24px',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SmallCountdown key={key} seconds={90} />
            <button 
              style={{
                ...buttonStyle,
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                fontSize: '16px',
                padding: '12px 24px'
              }}
              onClick={startActivity}
            >
              🔄 Ny utfordring
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Aktivitet: Hvem er jeg?
function WhoAmI() {
  const [currentHint, setCurrentHint] = useState(0);
  const [showTask, setShowTask] = useState(false);
  const [hints, setHints] = useState([]);
  const [answer, setAnswer] = useState("");
  const [usedTopics, setUsedTopics] = useState([]);

 const WHO_AM_I_TOPICS = [
  // --- Oppdaterte og mer utfordrende versjoner ---
  { answer: "En tannbørste", hints: ["Jeg har et hode, men ingen hjerne", "Jeg har mange hår, men er ikke et dyr", "Jeg trives best på badet", "Jeg jobber best med krem", "Min jobb er å fjerne spor etter mat"] },
 { answer: "En luftballong", hints: ["Jeg har ingen vinger, men kan likevel fly", "Jeg er avhengig av varme for å stige til værs", "Jeg reiser dit vinden tar meg", "Jeg har en kurv der folk kan stå", "Jeg drives av en stor flamme"] },
  { answer: "Et kompass", hints: ["Jeg har et ansikt, men ingen øyne", "Jeg vet alltid veien", "Jeg fungerer ikke godt nær store metallgjenstander", "Jeg er en turgåers beste venn når teknologien svikter", "Min nål peker alltid mot nord"] },
  
  // --- Klassikere (med justerte hint) ---
  { answer: "Pizza", hints: ["Jeg er en matrett", "Jeg kommer fra Napoli", "Jeg er ofte rund og deles i 'stykker'", "Mange liker meg best med smeltet ost på toppen", "Ananas på meg er en evig diskusjon"] },
  { answer: "Harry Potter", hints: ["Jeg er en fiktiv person", "Jeg er kjent for et arr i pannen", "Jeg har en ugle som kjæledyr", "Jeg går på en spesiell skole for magi", "Min største fiende er 'han-du-vet'"] },
{ answer: "Popcorn", hints: ["Jeg er mat", "Jeg lager lyd når jeg lages", "Jeg er vanlig på kino", "Jeg er laget av mais", "Jeg popper i varmen"] },

  // --- Finurlige Dyr & Natur ---
  { answer: "Kameleonen", hints: ["Jeg er kjent for å ikke skille meg ut", "Øynene mine kan se i to retninger samtidig", "Tungen min kan være lengre enn kroppen min", "Mange tror jeg skifter farge for å gjemme meg, men det er for å vise følelser", "Jeg er en type øgle"] },
  { answer: "Blekksprekk (en åttearmet)", hints: ["Jeg har tre hjerter og blått blod", "Hvis jeg mister en arm, vokser den ut igjen", "Jeg kan endre både farge og form på sekunder", "Jeg er smartere enn du tror og kan løse komplekse problemer", "Jeg bruker blekk som et røykteppe for å unnslippe fiender"] },
  { answer: "Et ekko", hints: ["Jeg snakker, men har ingen munn", "Jeg svarer alltid, men sier ikke noe nytt", "Jo lenger unna du er, jo lengre tid tar det før jeg svarer", "Jeg trives best i store, tomme rom eller i fjellet", "Jeg gjentar det siste du sa"] },
  { answer: "En vulkan", hints: ["Jeg kan være i dvale i hundrevis av år", "Jeg har et temperament som kan være eksplosivt", "Jeg kan skape nye landområder", "Jeg spyr ut aske og flytende stein", "Jeg ser ut som et fjell, men har et krater på toppen"] },

  // --- Ting & Tang (med feller) ---
  { answer: "Et speil", hints: ["Jeg har ingen munn, men kan 'svare' deg", "Jeg viser deg verden, men alltid baklengs", "Hvis du smiler til meg, smiler jeg alltid tilbake", "Jeg kan knuses i tusen biter", "Jeg viser deg alt, unntatt meg selv"] },
  { answer: "Et frimerke", hints: ["Jeg reiser verden rundt, men står alltid i et hjørne", "Jeg har ofte bilde av en konge eller natur", "Jeg må slikkes på ryggen for å gjøre jobben min", "Jeg hjelper brevet ditt med å komme frem", "Jeg settes øverst til høyre på en konvolutt"] },
  { answer: "En heis", hints: ["Jeg har dører, men er ikke et rom", "Jeg beveger meg bare i to retninger: opp og ned", "Jeg har knapper for hver etasje", "Jeg spiller ofte musikk mens du venter", "Jeg er en raskere måte å komme seg opp i en bygning på enn trapper"] },
  { answer: "En nøkkel", hints: ["Jeg har et hode og tenner, men kan ikke bite", "Jeg passer bare til ett bestemt sted", "Jeg kan åpne det som er låst", "Mister du meg, kan det bli et stort problem", "De fleste har meg i lomma eller på et knippe"] },

  // --- Konsepter & Teknologi ---
  { answer: "Tid", hints: ["Jeg kan gå, men har ingen føtter", "Alle har meg, men ingen kan eie meg", "Jeg kan måles, men ikke sees eller tas på", "Jeg kan 'fly' når du har det gøy", "Noen sier jeg leger alle sår"] },
  { answer: "En skygge", hints: ["Jeg følger deg overalt i solen, men forsvinner i mørket", "Jeg er alltid like stor eller større enn deg", "Du kan ikke fange meg, uansett hvor fort du løper", "Jeg har ingen vekt og lager ingen lyd", "Jeg blir lengst når lyset er lavest"] },
  { answer: "Internett", hints: ["Jeg er et sted uten en fysisk adresse", "Jeg inneholder nesten all kunnskap menneskeheten har samlet", "Jeg er et 'nett' som fanger informasjon, ikke fisk", "Uten meg ville smarttelefonen din vært ganske kjedelig", "Du bruker meg akkurat nå"] },
  { answer: "Kunstig intelligens (AI)", hints: ["Jeg kan skrive en historie, men har ingen fantasi", "Jeg kan lage et bilde, men har aldri holdt en pensel", "Jeg lærer av enorme mengder data", "Mange er både spente og litt redde for fremtiden min", "ChatGPT er en av mine mest kjente slektninger"] },
{ answer: "En drone", hints: ["Jeg kan fly, men har ingen pilot om bord", "Jeg blir ofte brukt til å ta bilder og video fra luften", "Jeg har fire eller flere propeller som snurrer fort", "Jeg styres med en fjernkontroll, ofte med en skjerm", "Jeg kan levere pakker eller brukes til overvåkning"] },
  
  // --- Norske Spesialiteter ---
  { answer: "Ostehøvelen", hints: ["Jeg er en norsk oppfinnelse fra 1925", "Jeg er skarp, men du kutter deg sjelden på meg", "Jeg brukes ofte til frokost og lunsj", "Jeg gjør flate ting enda flatere", "Jeg elsker brunost og norvegia"] },
  { answer: "Bunad", hints: ["Jeg er et klesplagg, men brukes bare ved spesielle anledninger", "Jeg kan fortelle hvor i Norge du eller familien din kommer fra", "Jeg er ofte arvegods og veldig kostbar", "Mange har på seg meg på 17. mai", "Jeg har ofte mye broderier og sølv"] },
  { answer: "Et troll", hints: ["Jeg er en figur fra norsk folketro", "Jeg bor i fjellet eller i mørke skoger", "Jeg tåler ikke sollys", "Jeg har ofte en stor nese og hale", "Noen ganger har jeg flere hoder"] },
  { answer: "Nordlyset", hints: ["Jeg er et lysfenomen på himmelen", "Jeg kan bare sees når det er mørkt og klart", "Jeg danser i grønne, lilla og rosa farger", "Jeg trives best langt mot nord", "Jeg skapes av partikler fra sola som treffer jordas atmosfære"] },

// Kjente personer/figurer
  { answer: "Albert Einstein", hints: ["Jeg er en person", "Jeg var vitenskapsmann", "Jeg hadde bustete hår", "Jeg utviklet relativitetsteorien", "Mitt etternavn brukes som synonym for 'smart'"] },
{ answer: "Mona Lisa", hints: ["Jeg er et kunstverk", "Jeg er et maleri", "Jeg smiler svakt", "Jeg henger i Louvre", "Jeg ble malt av Leonardo da Vinci"] },

// --- Geografi & Steder --- 
{ answer: "Amazonaselven", hints: ["Jeg er en elv", "Jeg renner gjennom en enorm regnskog", "Jeg har flere fiskearter enn noe annet elvesystem", "Jeg ligger i Sør-Amerika", "Jeg er verdens største elv målt i vannmengde"] }, 
{ answer: "Sahara", hints: ["Jeg er et geografisk område", "Jeg er ekstremt tørr og varm", "Jeg dekker store deler av Nord-Afrika", "Jeg er full av sand, men også stein og fjell", "Jeg er verdens største varme ørken"] }, 
{ answer: "Mount Everest", hints: ["Jeg er et sted på jorden", "Luften er veldig tynn på toppen av meg", "Mange drømmer om å bestige meg, men det er farlig", "Jeg ligger i fjellkjeden Himalaya", "Jeg er verdens høyeste fjell"] },
];

 function startActivity() {
    const availableTopics = usedTopics.length >= WHO_AM_I_TOPICS.length
      ? WHO_AM_I_TOPICS
      : WHO_AM_I_TOPICS.filter(topic => !usedTopics.find(used => used.answer === topic.answer));

    const topic = availableTopics[randInt(0, availableTopics.length - 1)];
    setHints(topic.hints);
    setAnswer(topic.answer);
    setCurrentHint(0);
    setShowTask(true);

    if (usedTopics.length >= WHO_AM_I_TOPICS.length) {
      setUsedTopics([topic]);
    } else {
      setUsedTopics([...usedTopics, topic]);
    }
  }

  function nextHint() {
    if (currentHint < hints.length - 1) {
      setCurrentHint(currentHint + 1);
    }
  }

  function showAnswer() {
    setCurrentHint(hints.length);
  }

  const buttonStyle = {
    padding: '12px 24px',
    borderRadius: '16px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        ❓ Hvem er jeg?
      </div>
      <p style={{
        color: '#cbd5e1',
        marginBottom: '32px',
        fontSize: '18px'
      }}>Gjett hva det er basert på hintene!</p>
      
      {!showTask ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <p style={{ fontSize: '20px', color: '#cbd5e1' }}>Klar til å gjette?</p>
          <button
            style={{
              ...buttonStyle,
              padding: '16px 32px',
              fontSize: '20px',
              background: 'linear-gradient(45deg, #0891b2, #0e7490)'
            }}
            onClick={startActivity}
          >
            🎭 Start gjettelek
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <div style={{
            background: 'linear-gradient(45deg, rgba(8, 145, 178, 0.2), rgba(14, 116, 144, 0.2))',
            border: '1px solid rgba(8, 145, 178, 0.3)',
            borderRadius: '24px',
            padding: '32px'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {hints.slice(0, currentHint + 1).map((hint, index) => (
                <div
                  key={index}
                  style={{
                    fontSize: '20px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                    padding: '16px'
                  }}
                >
                  <strong>Hint {index + 1}:</strong> {hint}
                </div>
              ))}
              
              {currentHint >= hints.length && (
                <div style={{
                  fontSize: '32px',
                  fontWeight: 'bold',
                  marginTop: '24px',
                  padding: '24px',
                  background: 'linear-gradient(45deg, rgba(34, 197, 94, 0.2), rgba(22, 163, 74, 0.2))',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  borderRadius: '16px'
                }}>
                  🎉 Svar: <span style={{ color: '#22c55e' }}>{answer}</span>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            {currentHint < hints.length - 1 && (
              <button
                style={{
                  ...buttonStyle,
                  background: 'linear-gradient(45deg, #2563eb, #1d4ed8)'
                }}
                onClick={nextHint}
              >
                💡 Neste hint
              </button>
            )}
            
            {currentHint < hints.length && (
              <button
                style={{
                  ...buttonStyle,
                  background: 'linear-gradient(45deg, #059669, #047857)'
                }}
                onClick={showAnswer}
              >
                🎯 Vis svar
              </button>
            )}
            
            <button
              style={{
                ...buttonStyle,
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
              onClick={startActivity}
            >
              🔄 Ny gjettelek
            </button>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '16px',
            maxWidth: '480px',
            margin: '0 auto'
          }}>
            <div style={{ fontSize: '14px', color: '#cbd5e1' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                💡 Slik spiller dere:
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, textAlign: 'left', fontSize: '13px' }}>
                <li style={{ marginBottom: '4px' }}>
                  Velg en tilfeldig elev eller et lag til å gjette.
                </li>
                <li style={{ marginBottom: '4px' }}>
                  De får bare én sjanse til å gjette, og målet er å få minst poeng.
                </li>                
                <li style={{ marginBottom: '4px' }}>
                  Poengberegning:
                  <ul>
                    <li>1 poeng for riktig svar på første hint</li>
                    <li>2 poeng for riktig svar på andre hint, osv.</li>
                    <li>10 poeng for feil svar</li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Aktivitet: Quiz
function QuickQuiz() {
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [usedQuestions, setUsedQuestions] = useState([]);

 const QUIZ_QUESTIONS = [
    { question: "Hvilket element har det kjemiske symbolet O?", answer: "Oksygen" },
    { question: "Hvor mange planeter er det i solsystemet?", answer: "8 planeter" },
    { question: "Hva heter den største planeten i solsystemet?", answer: "Jupiter" },
    { question: "Hvor mange ben har en edderkopp?", answer: "8 ben" },
    { question: "Hva er det største dyret i verden?", answer: "Blåhval" },
    { question: "Hvor mange kontinenter er det?", answer: "7 kontinenter" },
    { question: "Hvilken planet er kjent som den røde planeten?", answer: "Mars" },
    { question: "Hva kalles en vinkel på 90 grader?", answer: "Rett vinkel" },
    { question: "Hvilken gass utgjør størstedelen av atmosfæren?", answer: "Nitrogen" },
    { question: "Hva er det hardeste naturlige stoffet?", answer: "Diamant" },
    { question: "Hvor mange timer er det i en uke?", answer: "168 timer" },
    { question: "Hva er Norges nasjonalfugl?", answer: "Fossekallen" },
    { question: "Hvilken prosess bruker planter for å lage mat?", answer: "Fotosyntese" },
    { question: "Hva er den minste knokkelen i menneskekroppen?", answer: "Stigbøylen" },
    { question: "Hvor mange gram er det i et kilogram?", answer: "1000 gram" },
    { question: "I hvilket land ligger de kjente pyramidene ved Giza?", answer: "Egypt" },
    { question: "Hvilket hav er det største i verden?", answer: "Stillehavet" },
    { question: "Hvilken norsk konge ble drept i slaget på Stiklestad i 1030?", answer: "Olav den Hellige" },
    { question: "Hvem malte det berømte maleriet 'Skrik'?", answer: "Edvard Munch" },
    { question: "Hva heter hovedstaden i Australia?", answer: "Canberra" },
    { question: "Hvilket grunnstoff har det kjemiske symbolet Fe?", answer: "Jern" },
    { question: "Hva heter den lengste elva i verden?", answer: "Nilen" },
    { question: "Hvor mange dager er det i et skuddår?", answer: "366 dager" },
    { question: "Hvilket land har flest innbyggere i verden?", answer: "India" },
    { question: "Hva kalles studien av jordskjelv?", answer: "Seismologi" },
    { question: "Hvilket metall har det kjemiske symbolet Au?", answer: "Gull" },
    { question: "Hvilken enhet måles elektrisk strøm i?", answer: "Ampere" },
    { question: "Hva heter den største innsjøen i Norge?", answer: "Mjøsa" },
    { question: "Hvilket språk snakkes i Brasil?", answer: "Portugisisk" },
    { question: "Hva er hovedstaden i Canada?", answer: "Ottawa" },

    // Nye spørsmål
    { question: "Hva heter Norges lengste fjord?", answer: "Sognefjorden" },
    { question: "Hva er 12 x 8?", answer: "96" },
    { question: "Hvilket dyr kalles 'skogens konge' i Norge?", answer: "Elg" },
    { question: "Hvem skrev 'Romeo og Julie'?", answer: "William Shakespeare" },
    { question: "Hva heter den største verdensdelen?", answer: "Asia" },
    { question: "Hvilken planet ligger nærmest sola?", answer: "Merkur" },
    { question: "Hvor mange grader er det i en sirkel?", answer: "360 grader" },
    { question: "Hva er navnet på verdens høyeste fjell?", answer: "Mount Everest" },
    { question: "Hva heter den største øya i verden?", answer: "Grønland" },
    { question: "Hva er hovedstaden i Japan?", answer: "Tokyo" },
    { question: "Hvilken sport er Lionel Messi kjent for?", answer: "Fotball" },
    { question: "Hva heter den lengste elva i Norge?", answer: "Glomma" },
    { question: "Hva er hovedstaden i Frankrike?", answer: "Paris" },
    { question: "Hva heter verdens nest største hav?", answer: "Atlanterhavet" },
    { question: "Hva kalles læren om stjerner og planeter?", answer: "Astronomi" }
];

 function getNewQuestion() {
    const availableQuestions = usedQuestions.length >= QUIZ_QUESTIONS.length
      ? QUIZ_QUESTIONS
      : QUIZ_QUESTIONS.filter(q => !usedQuestions.find(used => used.question === q.question));

    const question = availableQuestions[randInt(0, availableQuestions.length - 1)];
    setCurrentQuestion(question);
    setShowAnswer(false);

    if (usedQuestions.length >= QUIZ_QUESTIONS.length) {
      setUsedQuestions([question]);
    } else {
      setUsedQuestions([...usedQuestions, question]);
    }
  }

  function handleButtonClick() {
    if (showAnswer) {
      getNewQuestion();
    } else {
      setShowAnswer(true);
    }
  }

  const buttonStyle = {
    padding: '16px 32px',
    borderRadius: '16px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
  };
  
  const dynamicButtonStyle = {
    ...buttonStyle,
    background: showAnswer
      ? 'linear-gradient(45deg, #2563eb, #1d4ed8)'
      : 'linear-gradient(45deg, #059669, #047857)',
    minWidth: '220px',
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        🧠 Quiz
      </div>
      <p style={{
        color: '#cbd5e1',
        marginBottom: '32px',
        fontSize: '18px'
      }}>Kjappe spørsmål for å teste kunnskapen!</p>
      
      {!currentQuestion ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <p style={{ fontSize: '20px', color: '#cbd5e1' }}>Klar for et spørsmål?</p>
          <button
            style={{
              ...buttonStyle,
              background: 'linear-gradient(45deg, #475569, #334155)'
            }}
            onClick={getNewQuestion}
          >
            🎯 Vis spørsmål
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <div style={{
            background: 'linear-gradient(45deg, rgba(71, 85, 105, 0.2), rgba(51, 65, 85, 0.2))',
            border: '1px solid rgba(71, 85, 105, 0.3)',
            borderRadius: '24px',
            padding: '32px'
          }}>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '24px',
              color: '#cbd5e1'
            }}>
              {currentQuestion.question}
            </div>
            
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginBottom: '24px' }}>
              <button
                style={dynamicButtonStyle}
                onClick={handleButtonClick}
              >
                {showAnswer ? '➡️ Neste spørsmål' : '💡 Vis svar'}
              </button>
            </div>
            
            {showAnswer && (
              <div style={{
                padding: '24px',
                background: 'linear-gradient(45deg, rgba(34, 197, 94, 0.2), rgba(22, 163, 74, 0.2))',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '16px'
              }}>
                <div style={{
                  fontSize: '18px',
                  color: '#22c55e',
                  marginBottom: '8px'
                }}>💡 Svar:</div>
                <div style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#22c55e'
                }}>
                  {currentQuestion.answer}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <button
              style={{
                ...buttonStyle,
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
              onClick={() => {setCurrentQuestion(null); setShowAnswer(false);}}
            >
              ⬅️ Tilbake
            </button>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '16px',
            maxWidth: '384px',
            margin: '0 auto'
          }}>
            <div style={{ fontSize: '14px', color: '#cbd5e1' }}>
              💡 Tips: Del opp i lag og bruk "tilfeldig trekk" for å velge hvilket lag som skal svare
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Aktivitet: Lykkehjul
function WheelOfNames() {
  const [state] = useAppState();
  const active = state.classes.find((c) => c.id === state.activeClassId) || state.classes[0];
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState("");
  const [rotation, setRotation] = useState(0);
  const [removedStudents, setRemovedStudents] = useState([]);

  const availableStudents = (active?.students || []).filter(student => !removedStudents.includes(student));

  function spinWheel() {
    if (spinning || !availableStudents.length) return;
    
    setSpinning(true);
    setResult("");
    
    // Først bestem hvor hjulet skal stoppe (tilfeldig vinkel)
    const extraSpins = randInt(3, 5) * 360;
    const randomStopAngle = randInt(0, 359);
    const finalRotation = rotation + extraSpins + randomStopAngle;
    
    setRotation(finalRotation);
    
    setTimeout(() => {
      // Beregn hvilken elev som er under pilen når hjulet stopper
      const normalizedAngle = finalRotation % 360;
      const anglePerStudent = 360 / availableStudents.length;
      
      // Siden pilen peker oppover og hjulet roterer med klokka,
      // må vi regne baklengs for å finne riktig sektor
      const adjustedAngle = (360 - normalizedAngle) % 360;
      const winnerIndex = Math.floor(adjustedAngle / anglePerStudent);
      const winner = availableStudents[winnerIndex];
      
      setResult(winner);
      setSpinning(false);
      beep(300, 1200);
    }, 2500);
  }

  function resetWheel() {
    setResult("");
    setRotation(0);
    setRemovedStudents([]);
  }

  function removeWinner() {
    if (result && !removedStudents.includes(result)) {
      setRemovedStudents([...removedStudents, result]);
      setResult("");
    }
  }

  if (!active?.students?.length) {
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{
          fontSize: '32px',
          fontWeight: 'bold',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px'
        }}>
          🎡 Lykkehjul
        </div>
        <p style={{
          color: '#cbd5e1',
          marginBottom: '32px',
          fontSize: '18px'
        }}>Legg til elever i elevlista for å bruke lykkehjulet!</p>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📝</div>
        <p style={{ color: '#64748b' }}>Gå til sidebar og legg til elever først.</p>
      </div>
    );
  }

  if (!availableStudents.length) {
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{
          fontSize: '32px',
          fontWeight: 'bold',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px'
        }}>
          🎡 Lykkehjul
        </div>
        <p style={{
          color: '#cbd5e1',
          marginBottom: '32px',
          fontSize: '18px'
        }}>Alle elever er valgt!</p>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
        <button 
          style={{
            padding: '16px 32px',
            borderRadius: '24px',
            fontWeight: 'bold',
            color: 'white',
            border: 'none',
            cursor: 'pointer',
            fontSize: '20px',
            background: 'linear-gradient(45deg, #16a34a, #15803d)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.3s ease'
          }}
          onClick={resetWheel}
        >
          🔄 Start på nytt
        </button>
      </div>
    );
  }

  const buttonStyle = {
    padding: '16px 32px',
    borderRadius: '24px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '20px',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        🎡 Lykkehjul
      </div>
      <p style={{
        color: '#cbd5e1',
        marginBottom: '32px',
        fontSize: '18px'
      }}>Snurr hjulet for å trekke en tilfeldig elev!</p>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
        <div style={{
          position: 'relative',
          margin: '0 auto',
          width: '320px',
          height: '320px'
        }}>
          <div style={{
            position: 'absolute',
            top: '0',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10
          }}>
            <div style={{
              width: '0',
              height: '0',
              borderLeft: '24px solid transparent',
              borderRight: '24px solid transparent',
              borderBottom: '48px solid #eab308',
              filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
            }}></div>
          </div>
          
          <div 
            style={{ 
              width: '320px', 
              height: '320px', 
              borderRadius: '50%', 
              border: '8px solid #475569', 
              position: 'relative', 
              overflow: 'hidden', 
              boxShadow: '0 16px 32px rgba(0, 0, 0, 0.4)',
              transform: `rotate(${rotation}deg)`,
              transition: spinning ? 'transform 2.5s cubic-bezier(0.17, 0.67, 0.12, 0.99)' : 'none',
              background: `conic-gradient(${availableStudents.map((_, index) => {
                const colors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#06b6d4'];
                const color = colors[index % colors.length];
                const start = (index / availableStudents.length) * 100;
                const end = ((index + 1) / availableStudents.length) * 100;
                return `${color} ${start}% ${end}%`;
              }).join(', ')})`
            }}
          >
            {availableStudents.map((student, index) => {
              const angle = (index / availableStudents.length) * 360 + (360 / availableStudents.length / 2);
              return (
                <div
                  key={student}
                  style={{
                    position: 'absolute',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.7)',
                    top: '50%',
                    left: '50%',
                    transform: `rotate(${angle}deg) translateY(-120px) rotate(-${angle}deg)`,
                    transformOrigin: '0 120px'
                  }}
                >
                  {student}
                </div>
              );
            })}
            
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '48px',
              height: '48px',
              background: '#334155',
              borderRadius: '50%',
              border: '4px solid #475569',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
            }}></div>
          </div>
        </div>

        {result && (
          <div style={{
            background: 'linear-gradient(45deg, rgba(34, 197, 94, 0.2), rgba(22, 163, 74, 0.2))',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: '24px',
            padding: '24px'
          }}>
            <div style={{
              fontSize: '18px',
              color: '#22c55e',
              marginBottom: '8px'
            }}>🎉 Vinner:</div>
            <div style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#22c55e',
              marginBottom: '16px'
            }}>
              {result}
            </div>
            <button 
              style={{
                ...buttonStyle,
                background: 'linear-gradient(45deg, #dc2626, #b91c1c)',
                fontSize: '16px',
                padding: '12px 24px'
              }}
              onClick={removeWinner}
            >
              ✅ Fjern fra hjul
            </button>
          </div>
        )}

        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
          <button
            style={{
              ...buttonStyle,
              background: spinning || !availableStudents.length 
                ? '#6b7280' 
                : 'linear-gradient(45deg, #dc2626, #b91c1c)',
              opacity: spinning || !availableStudents.length ? 0.6 : 1,
              cursor: spinning || !availableStudents.length ? 'not-allowed' : 'pointer'
            }}
            onClick={spinWheel}
            disabled={spinning || !availableStudents.length}
          >
            {spinning ? "🎡 Snurrer..." : "🎯 Snurr hjulet!"}
          </button>
          
          <button 
            style={{
              ...buttonStyle,
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              fontSize: '16px',
              padding: '12px 24px'
            }}
            onClick={resetWheel}
          >
            🔄 Nullstill
          </button>
        </div>

        <div style={{
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          padding: '16px',
          maxWidth: '384px',
          margin: '0 auto'
        }}>
          <div style={{ fontSize: '14px', color: '#cbd5e1' }}>
            📊 {availableStudents.length} av {active?.students?.length || 0} elever i hjulet
            {removedStudents.length > 0 && (
              <div style={{ marginTop: '8px', fontSize: '12px' }}>
                🎯 Valgt: {removedStudents.join(", ")}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Aktivitet: 6-plasser
function SixPlaces() {
  const [gameState, setGameState] = useState("setup"); // setup, playing, rolling, eliminated
  const [rolledNumber, setRolledNumber] = useState(null);
  const [round, setRound] = useState(1);

  function startGame() {
    setGameState("playing");
    setRolledNumber(null);
    setRound(1);
  }

  function rollDice() {
    if (gameState !== "playing") return;
    
    setGameState("rolling");
    setRolledNumber(null);
    
    // Animasjon av terning
    let rollCount = 0;
    const rollInterval = setInterval(() => {
      setRolledNumber(randInt(1, 6));
      rollCount++;
      
      if (rollCount >= 10) {
        clearInterval(rollInterval);
        const finalNumber = randInt(1, 6);
        setRolledNumber(finalNumber);
        setGameState("eliminated");
        beep(400, 800);
        
        // Auto-fortsett etter 4 sekunder (tid til å eliminere spillere)
        setTimeout(() => {
          setGameState("playing");
          setRound(prev => prev + 1);
        }, 4000);
      }
    }, 150);
  }

  function resetGame() {
    setGameState("setup");
    setRolledNumber(null);
    setRound(1);
  }

  const buttonStyle = {
    padding: '16px 32px',
    borderRadius: '16px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
  };

  return (
    <div style={{ textAlign: 'center', height: '100%' }}>
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        🎲 6-plasser
      </div>
      
      {gameState === "setup" ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <p style={{
            color: '#cbd5e1',
            fontSize: '18px',
            lineHeight: '1.6'
          }}>Still dere på tall 1-6 i klasserommet. Terningen bestemmer hvem som er ute!</p>
          
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            fontSize: '16px',
            color: '#cbd5e1'
          }}>
            <div style={{ marginBottom: '12px', fontWeight: 'bold', color: 'white' }}>📝 Spilleregler:</div>
            <div style={{ marginBottom: '8px' }}>• Elevene stiller seg på ett av tallene 1-6</div>
            <div style={{ marginBottom: '8px' }}>• Alle som står på tallet som kommer på terningen er ute</div>
            <div style={{ marginBottom: '8px' }}>• Mellom hver runde kan de gjenværende elevene flytte seg</div>
            <div>• Samme tall kan komme flere ganger på rad</div>
          </div>
          
          <button 
            style={{
              ...buttonStyle,
              background: 'linear-gradient(45deg, #ea580c, #c2410c)',
              fontSize: '20px'
            }}
            onClick={startGame}
          >
            🚀 Start spill
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Spillbrett - 3x2 grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: '16px',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            {[1, 2, 3, 4, 5, 6].map(number => {
              const isRolled = rolledNumber === number && gameState === "eliminated";
              
              return (
                <div 
                  key={number}
                  style={{
                    width: '120px',
                    height: '120px',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '48px',
                    fontWeight: 'bold',
                    color: 'white',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)',
                    transition: 'all 0.3s ease',
                    border: '3px solid',
                    background: isRolled 
                      ? 'linear-gradient(45deg, #dc2626, #b91c1c)'
                      : 'linear-gradient(45deg, #059669, #047857)',
                    borderColor: isRolled 
                      ? '#fbbf24' 
                      : '#22c55e',
                    boxShadow: isRolled 
                      ? '0 8px 24px rgba(220, 38, 38, 0.4), 0 0 0 4px rgba(251, 191, 36, 0.3)' 
                      : '0 4px 12px rgba(0, 0, 0, 0.3)',
                    transform: isRolled ? 'scale(1.1)' : 'scale(1)',
                    animation: isRolled ? 'pulse 1s infinite' : 'none'
                  }}
                >
                  {number}
                </div>
              );
            })}
          </div>

          {/* Terning */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '16px',
              background: 'linear-gradient(45deg, #fbbf24, #f59e0b)',
              border: '3px solid white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '48px',
              fontWeight: 'bold',
              color: 'white',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
              animation: gameState === "rolling" ? 'pulse 0.2s infinite' : 'none'
            }}>
              {rolledNumber || "?"}
            </div>
            
            {gameState === "eliminated" && rolledNumber && (
              <div style={{
                background: 'linear-gradient(45deg, rgba(220, 38, 38, 0.2), rgba(185, 28, 28, 0.2))',
                border: '1px solid rgba(220, 38, 38, 0.4)',
                borderRadius: '16px',
                padding: '16px',
                fontSize: '18px',
                fontWeight: 'bold'
              }}>
                🚨 Alle på tall {rolledNumber} er ute! Sett dere ned.
              </div>
            )}
          </div>

          {/* Spillinfo og kontroller */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                Runde: {round}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                style={{
                  ...buttonStyle,
                  background: gameState === "playing" 
                    ? 'linear-gradient(45deg, #ea580c, #c2410c)' 
                    : '#6b7280',
                  opacity: gameState === "playing" ? 1 : 0.6,
                  cursor: gameState === "playing" ? 'pointer' : 'not-allowed'
                }}
                onClick={rollDice}
                disabled={gameState !== "playing"}
              >
                {gameState === "rolling" ? "🎲 Kaster..." : "🎲 Kast terning"}
              </button>
              
              <button
                style={{
                  ...buttonStyle,
                  background: 'linear-gradient(45deg, #2563eb, #1d4ed8)'
                }}
                onClick={resetGame}
              >
                🔄 Nytt spill
              </button>
            </div>
            
            {gameState === "playing" && (
              <div style={{ fontSize: '14px', color: '#cbd5e1', textAlign: 'center' }}>
                💡 Tip: Elevene kan flytte seg mellom kastene!
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Aktivitet: Hangman
function Hangman() {
  const [gameState, setGameState] = useState("setup"); // setup, playing, won, lost
  const [secretWord, setSecretWord] = useState("");
  const [displayWord, setDisplayWord] = useState("");
  const [guessedLetters, setGuessedLetters] = useState([]);
  const [wrongGuesses, setWrongGuesses] = useState([]);
  const [inputWord, setInputWord] = useState("");

  const maxWrongGuesses = 6;
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZÆØÅ".split("");

  function startGame() {
    if (!inputWord.trim()) return;
    
    const word = inputWord.trim().toUpperCase();
    setSecretWord(word);
    setDisplayWord(word.split('').map(char => char === ' ' ? ' ' : '_').join(''));
    setGuessedLetters([]);
    setWrongGuesses([]);
    setGameState("playing");
    setInputWord("");
  }

  function guessLetter(letter) {
    if (gameState !== "playing" || guessedLetters.includes(letter)) return;
    
    const newGuessedLetters = [...guessedLetters, letter];
    setGuessedLetters(newGuessedLetters);
    
    if (secretWord.includes(letter)) {
      // Riktig gjetning - oppdater display
      const newDisplay = secretWord.split('').map(char => {
        if (char === ' ') return ' ';
        return newGuessedLetters.includes(char) ? char : '_';
      }).join('');
      
      setDisplayWord(newDisplay);
      
      // Sjekk om spilleren har vunnet
      if (!newDisplay.includes('_')) {
        setGameState("won");
        beep(600, 1200);
      }
    } else {
      // Feil gjetning
      const newWrongGuesses = [...wrongGuesses, letter];
      setWrongGuesses(newWrongGuesses);
      
      // Sjekk om spilleren har tapt
      if (newWrongGuesses.length >= maxWrongGuesses) {
        setGameState("lost");
        beep(800, 400);
      }
    }
  }

  function resetGame() {
    setGameState("setup");
    setSecretWord("");
    setDisplayWord("");
    setGuessedLetters([]);
    setWrongGuesses([]);
    setInputWord("");
  }

  function getHangmanDrawing() {
    const wrongCount = wrongGuesses.length;
    
    return (
      <svg width="200" height="250" viewBox="0 0 200 250" style={{ margin: '0 auto', display: 'block' }}>
        {/* Galge base - alltid synlig */}
        <rect x="10" y="220" width="80" height="20" fill="#8B4513" stroke="#654321" strokeWidth="2" />
        
        {/* Galge stolpe - alltid synlig */}
        <rect x="40" y="20" width="8" height="200" fill="#8B4513" stroke="#654321" strokeWidth="2" />
        
        {/* Galge toppbjelke - alltid synlig */}
        <rect x="40" y="20" width="100" height="8" fill="#8B4513" stroke="#654321" strokeWidth="2" />
        
        {/* Galge rope - alltid synlig */}
        <rect x="130" y="20" width="3" height="40" fill="#D2691E" />
        
        {/* Hode - vises ved 1+ feil */}
        {wrongCount >= 1 && (
          <circle 
            cx="131" 
            cy="75" 
            r="15" 
            fill={wrongCount >= 6 ? "#FF6B6B" : "#FFE4B5"} 
            stroke="#8B4513" 
            strokeWidth="2"
          />
        )}
        
        {/* Ansikt - vises ved 1+ feil */}
        {wrongCount >= 1 && wrongCount < 6 && (
          <g>
            {/* Øyne */}
            <circle cx="126" cy="70" r="2" fill="#000" />
            <circle cx="136" cy="70" r="2" fill="#000" />
            {/* Munn */}
            <path d="M 126 80 Q 131 85 136 80" stroke="#000" strokeWidth="1.5" fill="none" />
          </g>
        )}
        
        {/* Død ansikt - vises ved 6 feil */}
        {wrongCount >= 6 && (
          <g>
            {/* X øyne */}
            <g stroke="#000" strokeWidth="2">
              <line x1="123" y1="67" x2="129" y2="73" />
              <line x1="129" y1="67" x2="123" y2="73" />
              <line x1="133" y1="67" x2="139" y2="73" />
              <line x1="139" y1="67" x2="133" y2="73" />
            </g>
            {/* Tunge */}
            <ellipse cx="135" cy="82" rx="3" ry="6" fill="#FF69B4" />
          </g>
        )}
        
        {/* Kropp - vises ved 2+ feil */}
        {wrongCount >= 2 && (
          <rect x="128" y="90" width="6" height="50" fill="#4169E1" stroke="#000080" strokeWidth="1" />
        )}
        
        {/* Venstre arm - vises ved 3+ feil */}
        {wrongCount >= 3 && (
          <line 
            x1="128" 
            y1="100" 
            x2="115" 
            y2="120" 
            stroke="#8B4513" 
            strokeWidth="3"
            strokeLinecap="round"
          />
        )}
        
        {/* Høyre arm - vises ved 4+ feil */}
        {wrongCount >= 4 && (
          <line 
            x1="134" 
            y1="100" 
            x2="147" 
            y2="120" 
            stroke="#8B4513" 
            strokeWidth="3"
            strokeLinecap="round"
          />
        )}
        
        {/* Venstre ben - vises ved 5+ feil */}
        {wrongCount >= 5 && (
          <line 
            x1="129" 
            y1="140" 
            x2="120" 
            y2="165" 
            stroke="#8B4513" 
            strokeWidth="3"
            strokeLinecap="round"
          />
        )}
        
        {/* Høyre ben - vises ved 6 feil */}
        {wrongCount >= 6 && (
          <line 
            x1="133" 
            y1="140" 
            x2="142" 
            y2="165" 
            stroke="#8B4513" 
            strokeWidth="3"
            strokeLinecap="round"
          />
        )}
        
        {/* Skygge under galgen */}
        <ellipse cx="50" cy="235" rx="30" ry="5" fill="rgba(0,0,0,0.2)" />
      </svg>
    );
  }

  const buttonStyle = {
    padding: '12px 24px',
    borderRadius: '16px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
  };

  const letterButtonStyle = {
    padding: '8px 12px',
    borderRadius: '8px',
    fontWeight: 'bold',
    fontSize: '16px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    minWidth: '40px'
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        🎯 Hangman
      </div>
      
      {gameState === "setup" ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <p style={{
            color: '#cbd5e1',
            fontSize: '18px'
          }}>En elev kommer frem og skriver inn ordet som de andre skal gjette!</p>
          
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '16px'
          }}>
            <div style={{ fontSize: '16px', color: '#cbd5e1', marginBottom: '16px' }}>
              📝 Skriv inn ordet (skjules med ***):
            </div>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center' }}>
              <input 
                type="password"
                value={inputWord}
                onChange={(e) => setInputWord(e.target.value)}
                placeholder="Hemmelig ord..."
                style={{
                  width: '200px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  padding: '12px 16px',
                  color: 'white',
                  fontSize: '16px',
                  outline: 'none',
                  textAlign: 'center'
                }}
                onKeyDown={(e) => e.key === "Enter" && startGame()}
              />
              <button 
                style={{
                  ...buttonStyle,
                  background: 'linear-gradient(45deg, #7c2d12, #92400e)'
                }}
                onClick={startGame}
              >
                🚀 Start spill
              </button>
            </div>
          </div>
          
          <div style={{ fontSize: '14px', color: '#cbd5e1' }}>
            💡 Tips: Bruk enkle ord tilpasset alderen på elevene
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Hangman tegning */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            display: 'flex',
            justifyContent: 'center'
          }}>
            {getHangmanDrawing()}
          </div>

          {/* Ordet som gjettes */}
          <div style={{
            background: 'linear-gradient(45deg, rgba(124, 45, 18, 0.2), rgba(146, 64, 14, 0.2))',
            border: '1px solid rgba(124, 45, 18, 0.3)',
            borderRadius: '16px',
            padding: '32px'
          }}>
            <div style={{
              fontSize: '36px',
              fontWeight: 'bold',
              letterSpacing: '8px',
              fontFamily: 'Courier New, monospace',
              color: 'white',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)'
            }}>
              {displayWord}
            </div>
          </div>

          {/* Status */}
          {gameState === "won" && (
            <div style={{
              background: 'linear-gradient(45deg, rgba(34, 197, 94, 0.2), rgba(22, 163, 74, 0.2))',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '16px',
              padding: '24px'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#22c55e' }}>
                🎉 Gratulerer! Dere klarte det!
              </div>
            </div>
          )}

          {gameState === "lost" && (
            <div style={{
              background: 'linear-gradient(45deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2))',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '16px',
              padding: '24px'
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ef4444' }}>
                💀 Game Over! Ordet var: <span style={{ color: '#fbbf24' }}>{secretWord}</span>
              </div>
            </div>
          )}

          {/* Alfabet */}
          {gameState === "playing" && (
            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '16px',
              padding: '24px'
            }}>
              <div style={{ fontSize: '16px', color: '#cbd5e1', marginBottom: '16px' }}>
                Velg en bokstav:
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(40px, 1fr))',
                gap: '8px',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                {alphabet.map(letter => {
                  const isGuessed = guessedLetters.includes(letter);
                  const isWrong = wrongGuesses.includes(letter);
                  const isCorrect = isGuessed && !isWrong;
                  
                  return (
                    <button
                      key={letter}
                      style={{
                        ...letterButtonStyle,
                        background: isCorrect 
                          ? 'linear-gradient(45deg, #22c55e, #16a34a)'
                          : isWrong 
                            ? 'linear-gradient(45deg, #ef4444, #dc2626)'
                            : 'linear-gradient(45deg, #475569, #334155)',
                        color: 'white',
                        opacity: isGuessed ? 0.6 : 1,
                        cursor: isGuessed ? 'not-allowed' : 'pointer'
                      }}
                      onClick={() => guessLetter(letter)}
                      disabled={isGuessed}
                    >
                      {letter}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Feil bokstaver og kontroller */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '16px', fontWeight: '500' }}>
                Feil: {wrongGuesses.length}/{maxWrongGuesses}
                {wrongGuesses.length > 0 && (
                  <span style={{ color: '#ef4444', marginLeft: '12px' }}>
                    ({wrongGuesses.join(', ')})
                  </span>
                )}
              </div>
              <button
                style={{
                  ...buttonStyle,
                  background: 'linear-gradient(45deg, #2563eb, #1d4ed8)'
                }}
                onClick={resetGame}
              >
                🔄 Nytt spill
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Aktivitet: Dilemma
const DILEMMAS = [
  { left: "🍕 Pizza", right: "🍔 Hamburger" },
  { left: "🍿 Potetgull", right: "🍫 Sjokolade" },
  { left: "📊 Matte", right: "📖 Norsk" },
  { left: "🐕 Hund", right: "🐱 Katt" },
  { left: "☀️ Sommer", right: "❄️ Vinter" },
  { left: "🌅 Morgen", right: "🌙 Kveld" },
  { left: "🎬 Film", right: "📚 Bok" },
  { left: "⚽ Fotball", right: "🏀 Basketball" },
  { left: "🏔️ Fjell", right: "🏖️ Strand" },
   { left: "🍎 Eple", right: "🍌 Banan" },
  { left: "🎮 Spill", right: "🏃‍♀️ Sport" },
  { left: "🍦 Is", right: "🧁 Kake" },
  { left: "🎤 Synge", right: "💃 Danse" },
  { left: "🌧️ Regn", right: "☀️ Sol" },
  { left: "🛏️ Sove lenge", right: "🌅 Stå opp tidlig" },
  { left: "📱 TikTok", right: "📺 YouTube" },
  { left: "🍝 Pasta", right: "🍚 Ris" },
  { left: "🎪 Sirkus", right: "🎭 Teater" },
  { left: "🧩 Puslespill", right: "🎲 Brettspill" },
  { left: "🎸 Gitar", right: "🎹 Piano" },
  { left: "🥤 Brus", right: "🧃 Saft" },
  { left: "🎈 Bursdag", right: "🎄 Jul" },
  { left: "🦄 Enhjørning", right: "🐉 Drage" },
  { left: "🍕 Middag hjemme", right: "🍔 Restaurant" },
  { left: "📖 Lese", right: "✍️ Skrive" },
  { left: "🎢 Berg-og-dal-bane", right: "🎡 Pariserhjul" },
  { left: "🏊‍♀️ Svømming", right: "🚴‍♀️ Sykling" },
  { left: "🎭 Drama", right: "🔬 Naturfag" },
  { left: "🌮 Taco", right: "🍛 Wok" },
  { left: "🎪 Være publikum", right: "🎭 Være på scenen" },
  { left: "🎨 Tegne", right: "🏗️ Bygge" },
  { left: "🌊 Bølger", right: "⛰️ Topper" },
  { left: "🎵 Høre musikk", right: "🎤 Lage musikk" },
  { left: "🍓 Jordbær", right: "🫐 Blåbær" },
  { left: "🚗 Bil", right: "🚲 Sykkel" },
  { left: "🎪 Innendørs", right: "🏕️ Utendørs" },
  { left: "👥 Stor gruppe", right: "👫 Liten gruppe" },

  // Nye morsomme "umulige" dilemmaer
  { left: "🎤 Synge alt du sier", right: "🕺 Danse hver gang du beveger deg" },
  { left: "🍕 Spise bare pizza resten av livet", right: "🚫 Aldri spise pizza igjen" },
  { left: "📱 Mobilen spiller Bieber høyt ved hver melding", right: "🤳 Mobilen poster bilde hver gang du gjesper" },
  { left: "📢 Presentasjon foran hele skolen hver fredag", right: "⏰ Aldri friminutt lenger enn 5 minutter" },
  { left: "👩‍🏫 Læreren leser alle mobilmeldingene dine høyt", right: "👨‍👩‍👧 Foreldrene følger deg til skolen hver dag og sier «ha en fin dag, vennen»" },
  { left: "⏰ Skole fra 07:00–14:00", right: "🌙 Skole fra 12:00–19:00" },
  { left: "🧠 Kunne lese andres tanker", right: "🤥 Aldri kunne lyve igjen" },
  { left: "🍦 All mat smaker som is", right: "🥦 All mat smaker som brokkoli" },
  { left: "📺 Bare se samme film om og om igjen", right: "🎵 Bare høre samme sang om og om igjen" },
  { left: "📚 Gjøre lekser hver dag i sommerferien", right: "🏫 Gå på skolen annenhver lørdag" },
  { left: "🌍 Leve uten internett", right: "📞 Leve uten mobil" },
  { left: "🦸 Alltid være usynlig", right: "⚡ Alltid være supersynlig (alle legger merke til deg)" },
  { left: "👟 Alltid gå med to forskjellige sko", right: "👕 Alltid gå med klær bak-frem" },
  { left: "🛏️ Aldri være trøtt", right: "🍽️ Aldri være sulten" },
  { left: "🐵 Høres ut som en ape når du ler", right: "🦆 Høres ut som en and når du snakker" }
];

function Dilemma() {
  const [showDilemma, setShowDilemma] = useState(false);
  const [currentDilemma, setCurrentDilemma] = useState(null);
  const [key, setKey] = useState(0);
  const [usedDilemmas, setUsedDilemmas] = useState([]);

  function startDilemma() {
    const availableDilemmas = usedDilemmas.length >= DILEMMAS.length 
      ? DILEMMAS 
      : DILEMMAS.filter(d => !usedDilemmas.find(used => used.left === d.left && used.right === d.right));
    
    const dilemma = availableDilemmas[randInt(0, availableDilemmas.length - 1)];
    setCurrentDilemma(dilemma);
    setShowDilemma(true);
    setKey((k) => k + 1); // Reset timer
    
    if (usedDilemmas.length >= DILEMMAS.length) {
      setUsedDilemmas([dilemma]);
    } else {
      setUsedDilemmas([...usedDilemmas, dilemma]);
    }
  }

  const buttonStyle = {
    padding: '16px 32px',
    borderRadius: '24px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '20px',
    background: 'linear-gradient(45deg, #16a34a, #15803d)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
    transition: 'all 0.3s ease'
  };

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        ⚖️ Dilemma
      </div>
      <p style={{
        color: '#cbd5e1',
        marginBottom: '32px',
        fontSize: '18px'
      }}>Velg mellom to alternativer – gå til høyre eller venstre side av klasserommet!</p>
      
      {!showDilemma ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <p style={{ fontSize: '20px', color: '#cbd5e1' }}>Klar for et dilemma?</p>
          <button style={buttonStyle} onClick={startDilemma}>
            🎯 Vis dilemma
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            gap: '32px',
            alignItems: 'center',
            marginBottom: '32px'
          }}>
            {/* Venstre side */}
            <div style={{
              background: 'linear-gradient(45deg, rgba(37, 99, 235, 0.3), rgba(29, 78, 216, 0.3))',
              border: '2px solid rgba(37, 99, 235, 0.5)',
              borderRadius: '24px',
              padding: '48px 32px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '64px',
                marginBottom: '16px'
              }}>←</div>
              <div style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: 'white',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)'
              }}>
                {currentDilemma?.left}
              </div>
              <div style={{
                fontSize: '14px',
                color: 'white',
                marginTop: '12px',
                fontWeight: 'bold',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)'
              }}>
                VENSTRE SIDE
              </div>
            </div>

            {/* VS i midten */}
            <div style={{
              fontSize: '32px',
              fontWeight: 'bold',
              color: '#fbbf24',
              padding: '16px',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)'
            }}>
              VS
            </div>

            {/* Høyre side */}
            <div style={{
              background: 'linear-gradient(45deg, rgba(239, 68, 68, 0.3), rgba(220, 38, 38, 0.3))',
              border: '2px solid rgba(239, 68, 68, 0.5)',
              borderRadius: '24px',
              padding: '48px 32px',
              textAlign: 'center'
            }}>
              <div style={{
                fontSize: '64px',
                marginBottom: '16px'
              }}>→</div>
              <div style={{
                fontSize: '32px',
                fontWeight: 'bold',
                color: 'white',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.8)'
              }}>
                {currentDilemma?.right}
              </div>
              <div style={{
                fontSize: '14px',
                color: 'white',
                marginTop: '12px',
                fontWeight: 'bold',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)'
              }}>
                HØYRE SIDE
              </div>
            </div>
          </div>

          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div style={{ fontSize: '16px', color: '#cbd5e1' }}>
              <div style={{ marginBottom: '8px' }}>💭 Tenk deg godt om!</div>
              <div style={{ marginBottom: '8px' }}>👈 Velg venstre? Gå til venstre side av klasserommet</div>
              <div style={{ marginBottom: '8px' }}>👉 Velg høyre? Gå til høyre side av klasserommet</div>
              <div>⏱️ Dere har 10 sekunder til å bestemme dere!</div>
            </div>
          </div>

          <div style={{
            display: 'flex',
            gap: '24px',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <SmallCountdown key={key} seconds={10} autoStart={true} />
            <button 
              style={{
                ...buttonStyle,
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                fontSize: '16px',
                padding: '12px 24px'
              }}
              onClick={startDilemma}
            >
              🔄 Nytt dilemma
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Aktivitet: Fire lys
function FourLights() {
  const [gameState, setGameState] = useState("waiting");
  const [currentRound, setCurrentRound] = useState(1);
  const [activeLight, setActiveLight] = useState(null);
  const [lightPositions, setLightPositions] = useState([1, 2, 3, 4]);
  
  const baseLights = [
    { id: 1, emoji: "💨", instruction: "Ta ett skritt frem", color: "linear-gradient(45deg, #dc2626, #b91c1c)" },
    { id: 2, emoji: "⬅️", instruction: "Ta ett skritt tilbake", color: "linear-gradient(45deg, #059669, #047857)" },
    { id: 3, emoji: "🚫", instruction: "Ikke gjør noe", color: "linear-gradient(45deg, #eab308, #ca8a04)" },
    { id: 4, emoji: "🦆", instruction: "Sett deg på huk", color: "linear-gradient(45deg, #7c3aed, #6d28d9)" }
  ];

  const lights = lightPositions.map(pos => baseLights[pos - 1]);

  function startRound() {
    if (gameState !== "waiting") return;
    
    setGameState("starting");
    setActiveLight(null);
    
    if (currentRound >= 11) {
      setLightPositions(shuffle([1, 2, 3, 4]));
    }
    
    setTimeout(() => {
      setGameState("ready");
      
      const delay = Math.random() * 8000 + 4000;
      
      setTimeout(() => {
        const randomLight = Math.floor(Math.random() * 4) + 1;
        setActiveLight(randomLight);
        setGameState("active");
        beep(300, 1200);
        
        setTimeout(() => {
          setGameState("finished");
          
          setTimeout(() => {
            setCurrentRound(prev => prev + 1);
            setGameState("waiting");
            setActiveLight(null);
          }, 2000);
        }, 3000);
      }, delay);
    }, 1500);
  }

  function resetGame() {
    setGameState("waiting");
    setCurrentRound(1);
    setActiveLight(null);
    setLightPositions([1, 2, 3, 4]);
  }

  function getStatusText() {
    switch (gameState) {
      case "waiting":
        return "Trykk 'Start runde' for å begynne!";
      case "starting":
        return "Runden har startet...";
      case "ready":
        return "Vær klar... lyset kan komme når som helst!";
      case "active":
        return "🎯 Gjør det lyset sier!";
      case "finished":
        const light = baseLights.find(l => l.id === activeLight);
        let instruction = "";
        switch (light?.id) {
          case 1: instruction = "ta ett skritt frem"; break;
          case 2: instruction = "ta ett skritt tilbake"; break;
          case 3: instruction = "ikke gjøre noe"; break;
          case 4: instruction = "sette deg på huk"; break;
          default: instruction = "følge instruksjonen"; break;
        }
        return `Du skulle ${instruction}. Eliminer de som ikke gjorde dette, eller som var sist!`;
      default:
        return "";
    }
  }

  const showInstructions = currentRound <= 3;
  const showEmojis = currentRound <= 6;

  const buttonStyle = {
    padding: '16px 32px',
    borderRadius: '16px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
  };

  return (
    <div style={{ textAlign: 'center', height: '100%' }}>
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        marginBottom: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        💡 Fire lys
      </div>           
      <div style={{ marginBottom: '24px' }}>
        <div style={{
          fontSize: '22px',
          marginBottom: '24px',
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px'
        }}>
          {getStatusText()}
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '40px',
          maxWidth: '600px',
          margin: '0 auto 32px'
        }}>
          {lights.map((light, index) => {
            let lightStyle = {
              position: 'relative',
              width: '200px',
              height: '200px',
              borderRadius: '50%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              transition: 'all 0.5s ease',
              border: '4px solid',
              boxSizing: 'border-box'
            };

            if (gameState === "ready") {
              lightStyle = { 
                ...lightStyle, 
                background: '#374151', 
                borderColor: '#4b5563', 
                color: '#6b7280', 
                transform: 'scale(0.9)' 
              };
            } else if (gameState === "active" && activeLight === light.id) {
              lightStyle = { 
                ...lightStyle, 
                background: light.color, 
                borderColor: 'white', 
                boxShadow: '0 16px 32px rgba(0, 0, 0, 0.4)', 
                transform: 'scale(1.1)',
                animation: 'pulse 1s infinite'
              };
            } else if (gameState === "waiting" && showInstructions) {
              lightStyle = { 
                ...lightStyle, 
                background: light.color, 
                borderColor: '#9ca3af', 
                opacity: 0.8 
              };
            } else {
              lightStyle = { 
                ...lightStyle, 
                background: 'linear-gradient(45deg, #4b5563, #374151)', 
                borderColor: '#6b7280' 
              };
            }

            return (
              <div key={light.id} style={lightStyle}>
                {showEmojis && (
                  <div style={{ fontSize: '40px', marginBottom: '12px' }}>{light.emoji}</div>
                )}
                {showInstructions && (
                  <div style={{
                    fontSize: '16px',
                    textAlign: 'center',
                    padding: '0 16px',
                    lineHeight: '1.3'
                  }}>
                    {light.instruction}
                  </div>
                )}
                
                {activeLight === light.id && gameState === "active" && (
                  <div style={{
                    position: 'absolute',
                    inset: '0',
                    borderRadius: '50%',
                    border: '4px solid white',
                    animation: 'ping 1s infinite'
                  }}></div>
                )}
              </div>
            );
          })}
        </div>
        
        <div style={{
          display: 'flex',
          gap: '16px',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <button
            style={{
              ...buttonStyle,
              background: gameState === "waiting" 
                ? 'linear-gradient(45deg, #059669, #047857)' 
                : '#6b7280',
              opacity: gameState === "waiting" ? 1 : 0.6,
              cursor: gameState === "waiting" ? 'pointer' : 'not-allowed'
            }}
            onClick={startRound}
            disabled={gameState !== "waiting"}
          >
            🚀 Start runde
          </button>
          
          <button
            style={{
              ...buttonStyle,
              background: 'linear-gradient(45deg, #2563eb, #1d4ed8)'
            }}
            onClick={resetGame}
          >
            🔄 Nullstill spill
          </button>
        </div>
        
        <div style={{
          display: 'inline-block',
          background: 'rgba(255, 255, 255, 0.2)',
          padding: '8px 16px',
          borderRadius: '16px'
        }}>
          <span style={{ fontWeight: 'bold' }}>Runde: {currentRound}</span>
        </div>
      </div>
    </div>
  );
}

// --- Aktivitet: Tidslinje (Forbedret versjon)
const HISTORICAL_EVENTS = [
  // Verdenshistorie - antikken og middelalder
  { event: "Pyramidene i Egypt bygges", year: -2500, category: "Antikken", fact: "Den største pyramiden, Keopspyramiden, besto av ca. 2,3 millioner steinblokker." },
  { event: "Kristi fødsel", year: 0, category: "Antikken", fact: "Vår moderne kalender er basert på dette årstallet, selv om historikere mener Jesus ble født noen år tidligere." },
  { event: "Det vestromerske riket faller", year: 476, category: "Antikken", fact: "Dette regnes ofte som starten på middelalderen i Vest-Europa." },
  { event: "Vikingtiden begynner", year: 793, category: "Middelalder", fact: "Angrepet på klosteret i Lindisfarne, England, markerer tradisjonelt starten på vikingtiden." },
  { event: "Første korstog", year: 1095, category: "Middelalder", fact: "Etter en oppfordring fra Pave Urban II, dro europeiske riddere til Midtøsten for å erobre Jerusalem." },
  { event: "Svartedauden kom til Norge", year: 1349, category: "Middelalder", fact: "Pesten utslettet anslagsvis en tredjedel av Europas befolkning." },
  
  // Norge - middelalder og tidlig nyere tid
  { event: "Harald Hårfagre samler Norge", year: 872, category: "Norsk historie", fact: "Slaget i Hafrsfjord regnes som den avgjørende hendelsen for Norges samling til ett rike." },
  { event: "Kristendommen innføres i Norge", year: 1000, category: "Norsk historie", fact: "Selv om Olav Tryggvason startet kristningen, var det Olav den Hellige som fullførte den." },
  { event: "Slaget på Stiklestad", year: 1030, category: "Norsk historie", fact: "Olav den Hellige falt i dette slaget, noe som fikk stor betydning for kristningen av Norge." },
  { event: "Den norske union med Danmark begynner", year: 1380, category: "Norsk historie", fact: "Unionen, ofte kalt 400-årsnatten, varte helt til 1814." },
  { event: "Norge får egen grunnlov på Eidsvoll", year: 1814, category: "Norsk historie", fact: "Grunnloven ble signert 17. mai, som nå er Norges nasjonaldag." },
  { event: "Union med Sverige oppløses", year: 1905, category: "Norsk historie", fact: "Oppløsningen skjedde fredelig etter en folkeavstemning der nesten 100% stemte for." },
  
  // Oppdagelsesreiser og renessanse
  { event: "Columbus 'oppdager' Amerika", year: 1492, category: "Oppdagelser", fact: "Columbus trodde han hadde nådd India, og kalte derfor urbefolkningen for 'indianere'." },
  { event: "Gutenberg oppfinner boktrykkerkunsten", year: 1440, category: "Oppfinnelser", fact: "Dette gjorde det mulig å masseprodusere bøker og spre kunnskap raskere enn noensinne." },
  { event: "Leonardo da Vinci maler Mona Lisa", year: 1503, category: "Kunst", fact: "Maleriet er kjent for sitt mystiske smil og henger i Louvre-museet i Paris." },
  { event: "Magellan starter første verdensomseiling", year: 1519, category: "Oppdagelser", fact: "Magellan selv døde på Filippinene, men ett av hans skip fullførte reisen." },
  
  // 1600-1700-tallet
  { event: "Shakespeares død", year: 1616, category: "Litteratur", fact: "Han skrev minst 37 skuespill, inkludert klassikere som 'Hamlet' og 'Romeo og Julie'." },
  { event: "Isaac Newton publiserer 'Principia'", year: 1687, category: "Vitenskap", fact: "Boken la grunnlaget for klassisk mekanikk og formulerte lovene om tyngdekraft." },
  { event: "Den franske revolusjon begynner", year: 1789, category: "Politikk", fact: "Stormingen av Bastillen 14. juli er en sentral hendelse som markerer starten på revolusjonen." },
  
  // 1800-tallet
  { event: "Napoleon tapte ved Waterloo", year: 1815, category: "Krig", fact: "Nederlaget markerte slutten på Napoleonskrigene og hans keiserdømme." },
  { event: "Den første jernbanen åpnes", year: 1825, category: "Teknologi", fact: "Verdens første offentlige jernbane gikk mellom Stockton og Darlington i England." },
  { event: "Darwin publiserer 'Om artenes opprinnelse'", year: 1859, category: "Vitenskap", fact: "Boken presenterte teorien om evolusjon gjennom naturlig utvalg." },
  { event: "Alexander Graham Bell oppfinner telefonen", year: 1876, category: "Oppfinnelser", fact: "De første ordene som ble sagt i en telefon var 'Mr. Watson, come here, I want to see you'." },
  { event: "Edison oppfinner lyspæra", year: 1879, category: "Oppfinnelser", fact: "Thomas Edisons versjon var den første som var praktisk og langvarig nok for kommersiell bruk." },
  { event: "Eiffeltårnet bygges", year: 1889, category: "Arkitektur", fact: "Det ble bygget for Verdensutstillingen i Paris og var ment å være en midlertidig struktur." },
  
  // Tidlig 1900-tall
  { event: "Første verdenskrig begynner", year: 1914, category: "Krig", fact: "Krigen startet etter skuddene i Sarajevo, der erkehertug Franz Ferdinand ble myrdet." },
  { event: "Roald Amundsen når Sydpolen", year: 1911, category: "Oppdagelser", fact: "Han og hans team var de første menneskene som nådde Sydpolen, og de slo en britisk ekspedisjon." },
  { event: "Titanic synker", year: 1912, category: "Katastrofer", fact: "Skipet ble ansett som 'usynkelig' før det traff et isfjell på jomfruturen." },
  { event: "Brødrene Wright flyr første gang", year: 1903, category: "Teknologi", fact: "Den første vellykkede flyturen varte bare 12 sekunder og fløy 37 meter." },
  { event: "Første verdenskrig slutter", year: 1918, category: "Krig", fact: "Krigen endte med signeringen av våpenhvileavtalen klokken 11 den 11. november." },
  { event: "Kvinner får stemmerett i Norge", year: 1913, category: "Norsk historie", fact: "Norge var et av de første landene i verden som innførte allmenn stemmerett for kvinner." },
  
  // Mellomkrigstiden
  { event: "Alexander Fleming oppdager penicillin", year: 1928, category: "Medisin", fact: "Oppdagelsen skjedde ved en tilfeldighet da en bakteriekultur ble forurenset av muggsopp." },
  { event: "Børskrakket på Wall Street", year: 1929, category: "Økonomi", fact: "Krakket utløste 'Den store depresjonen', en verdensomspennende økonomisk krise." },
  { event: "Hitler kommer til makten i Tyskland", year: 1933, category: "Politikk", fact: "Han ble utnevnt til rikskansler, noe som markerte begynnelsen på Nazi-Tysklands regime." },
  
  // Andre verdenskrig
  { event: "Andre verdenskrig begynner", year: 1939, category: "Krig", fact: "Krigen startet da Tyskland invaderte Polen 1. september." },
  { event: "Tyskland invaderer Norge", year: 1940, category: "Norsk historie", fact: "Angrepet, kalt 'Operasjon Weserübung', skjedde 9. april." },
  { event: "Japan angriper Pearl Harbor", year: 1941, category: "Krig", fact: "Angrepet førte til at USA gikk med i andre verdenskrig." },
  { event: "D-dagen: Normandie-landgangen", year: 1944, category: "Krig", fact: "Den største amfibiske invasjonen i historien, som startet frigjøringen av Vest-Europa." },
  { event: "Atombomber slippes over Japan", year: 1945, category: "Krig", fact: "Bomben over Hiroshima var den første gangen et atomvåpen ble brukt i krig." },
  { event: "Andre verdenskrig slutter", year: 1945, category: "Krig", fact: "Tyskland kapitulerte i mai, og Japan kapitulerte i september etter atombombene." },
  
  // Kald krig-perioden
  { event: "FN etableres", year: 1945, category: "Politikk", fact: "Organisasjonen ble opprettet for å forhindre fremtidige verdenskriger." },
  { event: "Israel etableres som stat", year: 1948, category: "Politikk", fact: "Etableringen førte umiddelbart til krig med nabolandene." },
  { event: "NATO etableres", year: 1949, category: "Politikk", fact: "En forsvarsallianse mellom USA, Canada og vesteuropeiske land som svar på Sovjetunionens makt." },
  { event: "Korean-krigen begynner", year: 1950, category: "Krig", fact: "En konflikt mellom Nord-Korea (støttet av Kina og Sovjet) og Sør-Korea (støttet av FN/USA)." },
  { event: "DNA-strukturen oppdages", year: 1953, category: "Vitenskap", fact: "James Watson og Francis Crick publiserte sin modell av dobbeltheliks-strukturen." },
  { event: "Sputnik sendes opp", year: 1957, category: "Romfart", fact: "Sovjetunionens oppskytning av den første satellitten startet 'romkappløpet' med USA." },
  { event: "Berlinmuren bygges", year: 1961, category: "Politikk", fact: "Muren ble bygget for å stoppe folk fra å flykte fra Øst- til Vest-Berlin." },
  { event: "John F. Kennedy skutt", year: 1963, category: "Politikk", fact: "Attentatet i Dallas, Texas, sjokkerte verden og er fortsatt gjenstand for konspirasjonsteorier." },
  { event: "Martin Luther King holder 'I have a dream'-talen", year: 1963, category: "Borgerrettigheter", fact: "Talen ble holdt under 'Marsjen mot Washington' og er et ikonisk øyeblikk i borgerrettighetskampen." },
  { event: "Neil Armstrong går på månen", year: 1969, category: "Romfart", fact: "Hans berømte ord var: 'That's one small step for [a] man, one giant leap for mankind'." },
  { event: "Woodstock-festivalen", year: 1969, category: "Kultur", fact: "En ikonisk musikkfestival som samlet over 400 000 mennesker og definerte en generasjon." },
  
  // 1970-80-tallet
  { event: "Vietnam-krigen slutter", year: 1975, category: "Krig", fact: "Krigen endte med at Nord-Vietnam erobret Sør-Vietnam, og landet ble gjenforent under kommunistisk styre." },
  { event: "Apple lanserer første datamaskin", year: 1976, category: "Teknologi", fact: "Apple I var en av de første personlige datamaskinene, og ble solgt som et byggesett." },
  { event: "Tsjernobyl-ulykken", year: 1986, category: "Katastrofer", fact: "Den verste atomkraftulykken i historien, med radioaktivt nedfall over store deler av Europa." },
  { event: "Berlinmuren faller", year: 1989, category: "Politikk", fact: "Murens fall symboliserte slutten på den kalde krigen og førte til Tysklands gjenforening." },
  
  // 1990-tallet
  { event: "Sovjetunionen oppløses", year: 1991, category: "Politikk", fact: "Oppløsningen markerte den endelige slutten på den kalde krigen." },
  { event: "Internett (www) oppfinnes", year: 1991, category: "Teknologi", fact: "Oppfunnet av Tim Berners-Lee ved CERN for å hjelpe forskere med å dele informasjon." },
  { event: "Nelson Mandela blir president i Sør-Afrika", year: 1994, category: "Politikk", fact: "Dette markerte slutten på apartheid-systemet etter at Mandela hadde sittet 27 år i fengsel." },
  { event: "Den andre norske EU-avstemningen", year: 1994, category: "Norsk historie", fact: "Resultatet var et knapt nei-flertall, med '52,2%' som stemte mot norsk medlemskap og '47,8%' for" },
  { event: "Prinsesse Diana dør", year: 1997, category: "Berømtheter", fact: "Hennes død i en bilulykke i Paris utløste en verdensomspennende sorgreaksjon." },
  
  // 2000-tallet
  { event: "Terrorangrepet 11. september", year: 2001, category: "Terrorisme", fact: "Fire passasjerfly ble kapret; to traff World Trade Center i New York." },
  { event: "iPhone lanseres", year: 2007, category: "Teknologi", fact: "Den første iPhonen revolusjonerte mobiltelefonmarkedet med sin store berøringsskjerm." },
  { event: "Barack Obama blir USAs president", year: 2009, category: "Politikk", fact: "Han var den første afroamerikanske presidenten i USAs historie." },
  
  // 2010-tallet
  { event: "Facebook når 1 milliard brukere", year: 2012, category: "Teknologi", fact: "En milepæl for sosiale medier, som viste hvor integrert det hadde blitt i folks liv." },
  { event: "Donald Trump blir USAs president første gang", year: 2017, category: "Politikk", fact: "Hans valgseier ble ansett som en stor overraskelse i politiske kretser." },
  { event: "Brexit-avstemningen", year: 2016, category: "Politikk", fact: "Storbritannia stemte for å forlate Den europeiske union (EU)." },
  
  // 2020-tallet
  { event: "COVID-19 pandemien begynner", year: 2020, category: "Helse", fact: "Verdens helseorganisasjon (WHO) erklærte utbruddet som en global pandemi 11. mars 2020." },
  { event: "Joe Biden blir USAs president", year: 2021, category: "Politikk", fact: "Han ble den eldste personen som noensinne er innsatt som amerikansk president." },
  
  // Norske spesielle hendelser
  { event: "Olje oppdages i Nordsjøen", year: 1969, category: "Norsk historie", fact: "Funnet av Ekofisk-feltet forvandlet Norge til en av verdens rikeste nasjoner." },
  { event: "OL i Lillehammer", year: 1994, category: "Norsk historie", fact: "Vinter-OL i 1994 regnes som et av de mest vellykkede i historien." },
  { event: "22. juli-angrepene", year: 2011, category: "Norsk historie", fact: "Terrorangrepene i Oslo og på Utøya er den verste voldshandlingen i Norge siden andre verdenskrig." }
];

function Timeline() {
  const [gameState, setGameState] = useState("setup");
  const [events, setEvents] = useState([]);
  const [userOrder, setUserOrder] = useState([]);
  const [usedEventSets, setUsedEventSets] = useState([]);
  const [isCorrect, setIsCorrect] = useState(false);
  const setupRef = useRef(null); // Gjeninnfører ref for fokus

  // Gjeninnfører useEffect for fokus på setup
  useEffect(() => {
    if (gameState === 'setup' && setupRef.current) {
      setupRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [gameState]);

  function generateEventSet(numEvents) {
    const availableEvents = HISTORICAL_EVENTS.filter(event =>
      !usedEventSets.some(usedSet =>
        usedSet.some(usedEvent => usedEvent.event === event.event)
      )
    );
    if (availableEvents.length < numEvents) {
      setUsedEventSets([]);
      return shuffle(HISTORICAL_EVENTS).slice(0, numEvents);
    }
    const selectedEvents = shuffle(availableEvents).slice(0, numEvents);
    setUsedEventSets(prev => [...prev, selectedEvents]);
    return selectedEvents;
  }

  function startGame(numEvents) {
    const selectedEvents = generateEventSet(numEvents);
    setEvents(shuffle(selectedEvents));
    setUserOrder([]);
    setIsCorrect(false);
    setGameState("playing");
  }

  function handleEventClick(event) {
    if (gameState !== "playing") return;
    if (userOrder.includes(event)) {
      setUserOrder(userOrder.filter(e => e !== event));
    } else {
      setUserOrder([...userOrder, event]);
    }
  }

 function checkAnswer() {
  const correctOrder = [...events].sort((a, b) => a.year - b.year);
  const isAnswerCorrect = userOrder.length === events.length &&
    userOrder.every((event, index) => {
      // Finn alle hendelser med samme år som den korrekte på denne posisjonen
      const correctYear = correctOrder[index].year;
      const eventsWithSameYear = events.filter(e => e.year === correctYear);
      return eventsWithSameYear.includes(event);
    });
  setIsCorrect(isAnswerCorrect);
  setGameState("finished");
  if (isAnswerCorrect) { beep(600, 1200); } else { beep(400, 800); }
}

  function resetGame() {
    setGameState("setup");
    setEvents([]);
    setUserOrder([]);
  }

  const buttonStyle = {
    padding: '16px 32px',
    borderRadius: '24px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '20px',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
  };

  const eventCardStyle = {
    background: 'linear-gradient(45deg, #475569, #334155)',
    border: '2px solid #64748b',
    borderRadius: '16px',
    padding: '20px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textAlign: 'center',
    fontSize: '18px',
    fontWeight: '500',
    lineHeight: '1.4',
    minHeight: '60px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  return (
    <div style={{ width: '100%', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '16px' }}>
          📅 Tidslinje
        </div>
        <p style={{ color: '#cbd5e1', fontSize: '18px' }}>
          Sorter hendelsene i riktig tidsrekkefølge, fra eldst til nyest!
        </p>
      </div>
      
      {gameState === "setup" ? (
        <div ref={setupRef} style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '24px', fontWeight: 'bold', color: 'white', marginBottom: '24px' }}>
            Velg vanskelighetsgrad
          </div>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button style={{ ...buttonStyle, background: 'linear-gradient(45deg, #22c55e, #16a34a)' }} onClick={() => startGame(3)}>
              3 hendelser
            </button>
            <button style={{ ...buttonStyle, background: 'linear-gradient(45deg, #f59e0b, #d97706)' }} onClick={() => startGame(4)}>
              4 hendelser
            </button>
            <button style={{ ...buttonStyle, background: 'linear-gradient(45deg, #ef4444, #dc2626)' }} onClick={() => startGame(5)}>
              5 hendelser
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '40px', marginBottom: '32px' }}>
            <div style={{ minWidth: '0' }}>
              <h3 style={{ fontSize: '24px', color: '#a78bfa', textAlign: 'center', marginBottom: '20px', fontWeight: 'bold' }}>
                📋 Hendelser
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {events.filter(e => !userOrder.includes(e)).map(event => (
                  <div 
                    key={event.event} 
                    style={{
                      ...eventCardStyle,
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                    }} 
                    onClick={() => handleEventClick(event)}
                    onMouseOver={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 8px 20px rgba(0, 0, 0, 0.3)';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.2)';
                    }}
                  >
                    {event.event}
                  </div>
                ))}
              </div>
            </div>
            
            <div style={{ minWidth: '0' }}>
              <h3 style={{ fontSize: '24px', color: '#fbbf24', textAlign: 'center', marginBottom: '20px', fontWeight: 'bold' }}>
                ⏰ Din Tidslinje
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {userOrder.length > 0 ? userOrder.map((event, index) => {
                  const correctOrder = [...events].sort((a, b) => a.year - b.year);
                  const isPlacedCorrectly = correctOrder[index]?.event === event.event;
                  let cardStyle = { 
                    ...eventCardStyle, 
                    background: 'linear-gradient(45deg, #3b82f6, #2563eb)', 
                    borderColor: '#60a5fa',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    minHeight: 'auto'
                  };
                  if (gameState === "finished") {
                    cardStyle.background = isPlacedCorrectly ? 
                      'linear-gradient(45deg, #059669, #047857)' : // Mørkere grønn
                      'linear-gradient(45deg, #ef4444, #dc2626)';
                    cardStyle.borderColor = isPlacedCorrectly ? '#10b981' : '#f87171';
                    cardStyle.boxShadow = isPlacedCorrectly ? 
                      '0 4px 12px rgba(5, 150, 105, 0.4)' : 
                      '0 4px 12px rgba(239, 68, 68, 0.3)';
                  }
                  return (
                    <div key={event.event} style={cardStyle} onClick={() => handleEventClick(event)}>
                      <div style={{ 
                        fontWeight: 'bold', 
                        fontSize: '18px', 
                        marginBottom: gameState === 'finished' ? '12px' : '0',
                        color: 'white' // Hvit tekst igjen
                      }}>
                        {index + 1}. {event.event}
                      </div>
                      {gameState === 'finished' && (
                        <div style={{ 
                          borderTop: '1px solid rgba(255, 255, 255, 0.2)', 
                          paddingTop: '12px',
                          textAlign: 'left'
                        }}>
                          <div style={{ 
                            fontSize: '16px', 
                            fontWeight: 'bold', 
                            color: 'white', // Hvit tekst igjen
                            marginBottom: '8px' 
                          }}>
                            {event.year < 0 ? `${Math.abs(event.year)} f.Kr.` : `År ${event.year}`}
                          </div>
                          <div style={{ 
                            fontSize: '14px', 
                            color: 'rgba(255, 255, 255, 0.9)', // Hvit tekst igjen
                            lineHeight: '1.5' 
                          }}>
                            {event.fact}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }) : (
                  <div style={{ 
                    color: '#94a3b8', 
                    fontSize: '16px', 
                    padding: '40px 20px', 
                    border: '2px dashed #475569', 
                    borderRadius: '16px', 
                    textAlign: 'center',
                    background: 'rgba(255, 255, 255, 0.05)'
                  }}>
                    Klikk på hendelser til venstre for å bygge tidslinjen din her.
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div style={{ textAlign: 'center' }}>
            {gameState === "playing" && (
              <button 
                style={{ 
                  ...buttonStyle, 
                  background: userOrder.length === events.length ? 
                    'linear-gradient(45deg, #059669, #047857)' : '#6b7280',
                  opacity: userOrder.length === events.length ? 1 : 0.6,
                  cursor: userOrder.length === events.length ? 'pointer' : 'not-allowed',
                  marginBottom: '16px'
                }} 
                onClick={checkAnswer} 
                disabled={userOrder.length !== events.length}
              >
                ✅ Sjekk svar
              </button>
            )}
            
            {gameState === "finished" && (
              <div style={{ 
                background: isCorrect ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                border: `2px solid ${isCorrect ? 'rgba(34, 197, 94, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
                borderRadius: '16px',
                padding: '16px 24px',
                fontSize: '22px',
                fontWeight: 'bold',
                color: isCorrect ? '#22c55e' : '#ef4444',
                marginBottom: '16px',
                display: 'inline-block'
              }}>
                {isCorrect ? '🎉 Helt riktig!' : '❌ Ikke helt riktig. Se fasit over.'}
              </div>
            )}
            
            <div>
              <button 
                style={{ ...buttonStyle, background: 'linear-gradient(45deg, #8b5cf6, #7c3aed)' }} 
                onClick={resetGame}
              >
                🔄 Start ny runde
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Aktivitet: Fakta eller Myte
const FACT_MYTH_STATEMENTS = [
  // Sanne påstander
  { statement: "Haier er faktisk fisk, ikke sjødyr", isTrue: true, explanation: "Haier tilhører klassen bruskfisk og er dermed fisk, selv om mange tror de er sjødyr." },
  { statement: "Bananer er teknisk sett bær", isTrue: true, explanation: "Botanisk sett er bananer klassifisert som bær, mens jordbær ikke er det." },
  { statement: "Honning kan aldri bli dårlig", isTrue: true, explanation: "Arkeologer har funnet spiselig honning i egyptiske graver som er over 3000 år gamle." },
    { statement: "Blekkspruten har tre hjerter", isTrue: true, explanation: "To hjerter pumper blod til gjellene, mens det tredje pumper blod til resten av kroppen." },
  { statement: "Det er flere mulige sjakkpartier enn atomer i det observerbare universet", isTrue: true, explanation: "Antallet mulige sjakkpartier er så enormt stort at det overgår antallet atomer i universet." },
  { statement: "Krokodiller kan ikke stikke tunga ut", isTrue: true, explanation: "Krokodiller har tunga fast til gummen, så de kan ikke strekke den ut." },
  { statement: "Norge har flere øyer enn Finland", isTrue: true, explanation: "Norge har rundt 239 000 øyer, mens Finland har ca. 179 000 øyer. (Dette kan variere avhengig av definisjon)" },
  { statement: "En gruppe ugler kalles et 'parliament'", isTrue: true, explanation: "På engelsk kalles en flokk ugler for et 'parliament', muligens på grunn av deres kloke utseende." },
  { statement: "Wombat-avføring er firkantet", isTrue: true, explanation: "Wombater er de eneste dyrene som produserer firkantet avføring, takket være deres unike tarmsystem." },
  
  // Falske påstander
  { statement: "Mennesker bruker kun 10% av hjernen sin", isTrue: false, explanation: "Dette er en myte. Vi bruker nesten hele hjernen vår, selv under enkle oppgaver." },
  { statement: "Lynet slår aldri ned på samme sted to ganger", isTrue: false, explanation: "Lynet kan absolutt slå ned på samme sted flere ganger. Empire State Building treffes ca. 100 ganger i året." },
  { statement: "Kameleoner skifter farge for å kamuflere seg", isTrue: false, explanation: "Kameleoner skifter farge hovedsakelig for å kommunisere følelser og regulere temperatur, ikke kamuflasje." },
    { statement: "Gullfisk har kun 3 sekunders hukommelse", isTrue: false, explanation: "Gullfisk kan faktisk huske ting i måneder, ikke bare sekunder." },
  { statement: "Vi har kun fem sanser", isTrue: false, explanation: "Mennesker har mange flere sanser, inkludert balanse, temperatur, smerte, og kroppens posisjon i rommet." },
  { statement: "Hår og negler fortsetter å vokse etter døden", isTrue: false, explanation: "Dette er en illusjon som oppstår fordi huden trekker seg sammen etter døden, slik at hår og negler ser lengre ut." },
  { statement: "Lyn slår alltid oppover fra bakken", isTrue: false, explanation: "Lyn kan bevege seg i begge retninger - både fra sky til bakke og fra bakke til sky." },
  { statement: "Man kan se den kinesiske mur fra månen", isTrue: false, explanation: "Dette er en gjenganger myte. Den kinesiske mur er ikke synlig fra månen uten tekniske hjelpemidler." },
  { statement: "Alle snøflak er unike", isTrue: false, explanation: "Selv om sjansen er liten, er det teoretisk mulig at to snøflak kan være identiske." },
  
  // Norske/nordiske påstander
  { statement: "Norge har verdens lengste kystlinje", isTrue: false, explanation: "Canada har faktisk verdens lengste kystlinje, ikke Norge." },
  { statement: "Det norske ordet 'ski' kommer fra det gamle norrøne ordet 'skíð'", isTrue: true, explanation: "Ordet ski kommer faktisk fra gammelnorsk og betyr 'spaltet ved' eller 'trestykke'." },
  { statement: "Vikingene hadde horn på hjelmene sine", isTrue: false, explanation: "Dette er en moderne myte. Arkeologiske funn viser at vikinghjelmer ikke hadde horn." },
  { statement: "Norge oppfant ostehøvelen", isTrue: true, explanation: "Thor Bjørklund oppfant ostehøvelen i 1925." },
  { statement: "Island har flest aktive vulkaner i Europa", isTrue: true, explanation: "Island ligger på den mid-atlantiske rygg og har over 30 aktive vulkaner." },
  
  // Vitenskapelige påstander
  { statement: "Diamant er det hardeste materialet på jorden", isTrue: false, explanation: "Wutzitt og lonsdaleitt (begge former for karbon) kan være hardere enn diamant." },
  { statement: "Det er varmere på Venus enn på Merkur", isTrue: true, explanation: "Selv om Merkur er nærmere solen, er Venus varmere på grunn av drivhuseffekten." },
  { statement: "Antibiotika virker mot virus", isTrue: false, explanation: "Antibiotika virker kun mot bakterier, ikke virus. Det er derfor de ikke hjelper mot forkjølelse eller influensa." },
  { statement: "Det tar 7 år å fordøye tyggegummi", isTrue: false, explanation: "Kroppen kan ikke fordøye tyggegummi, men det passerer gjennom systemet på noen få dager." },
  { statement: "Gravitasjonen på månen er 1/6 av jordens gravitasjon", isTrue: true, explanation: "Månens gravitasjon er ca. 16,5% av jordens, ofte avrundet til 1/6." },
  
  // Dyre-påstander  
  { statement: "Struts gjemmer hodet i sanden når de er redde", isTrue: false, explanation: "Strutser gjør ikke dette. De kan løpe opptil 70 km/t når de er i fare." },
  { statement: "Flaggermus er blinde", isTrue: false, explanation: "De fleste flaggermus kan faktisk se ganske godt, i tillegg til å bruke ekkolokalisering." },
  { statement: "Elefanter er redde for mus", isTrue: false, explanation: "Det er ingen vitenskapelig bevis for at elefanter er redde for mus." },
  { statement: "Pandas er ikke bjørn", isTrue: false, explanation: "Store pandas tilhører bjørnefamilien, selv om de var omdiskuterte tidligere." },
  { statement: "Humler kan teoretisk sett ikke fly", isTrue: false, explanation: "Denne myten oppsto fra en misforståelse av aerodynamikk. Humler kan absolutt fly." },
  
  // Historiske påstander
  { statement: "Napoleon var uvanlig kort for sin tid", isTrue: false, explanation: "Napoleon var ca. 170 cm, som var gjennomsnittlig høyde for menn på 1700-tallet." },
  { statement: "Middelalderfolk trodde jorden var flat", isTrue: false, explanation: "De fleste utdannede mennesker i middelalderen visste at jorden var rund." },
  { statement: "Columbus beviste at jorden var rund", isTrue: false, explanation: "Grekerne visste at jorden var rund flere hundre år før Kolumbus." },

 // Sanne påstander
  { statement: "Flamingoer får sin rosa farge fra maten de spiser", isTrue: true,
    explanation: "Fargen kommer fra karotenoider i krepsdyr og alger i kostholdet, ikke fra selve fjærveksten." },

  { statement: "Mennesker og sjimpanser deler omtrent 98-99% av DNA-et sitt", isTrue: true,
    explanation: "Genetiske studier viser at sjimpanser er våre nærmeste slektninger med svært høy DNA-likhet (omtrent 98-99%)." },

  { statement: "Vann utvider seg når det fryser", isTrue: true,
    explanation: "Is har lavere tetthet enn flytende vann fordi molekylstrukturen danner et gitter, derfor flyter is på vann." },

  { statement: "Solen er en stjerne", isTrue: true,
    explanation: "Solen er en mediumstor stjerne (en gul dverg) og fungerer som vårt solsystems primære energikilde." },

  { statement: "Jupiter er den største planeten i solsystemet vårt", isTrue: true,
    explanation: "Jupiter har størst masse og volum av planetene i solsystemet." },

  { statement: "Lyn kan være varmere enn solens overflate", isTrue: true,
    explanation: "Temperaturen i et lyn kan nå titusenvis av grader Celsius — høyere enn solens fotosfære (~5 800 °C)." },

  { statement: "Kameler lagrer fett i puklene, ikke vann", isTrue: true,
    explanation: "Puklene består hovedsakelig av fett; dette gir energireserver. Kameler er likevel svært tilpasset vannlagring og -bruk." },

  { statement: "Månen har i praksis ingen atmosfære", isTrue: true,
    explanation: "Månen har bare en svært tynn eksosfære, så den har ikke en betydelig atmosfære som jorden." },

  { statement: "Pluto er klassifisert som en dvergplanet", isTrue: true,
    explanation: "Siden 2006 har Den internasjonale astronomiske union (IAU) klassifisert Pluto som en dvergplanet." },

  { statement: "Ozonlaget beskytter jorden mot skadelig UV-stråling", isTrue: true,
    explanation: "Ozon (O3) i stratosfæren absorberer store deler av solens ultrafiolette stråling og beskytter levende organismer." },

  { statement: "Blod er rødt på grunn av hemoglobin", isTrue: true,
    explanation: "Hemoglobin i røde blodlegemer inneholder jern og gir blodet den karakteristiske røde fargen når det binder oksygen." },

  { statement: "Noen stener kan flyte på vann", isTrue: true,
    explanation: "Pumice (stein fra vulkansk lava med mye luft) kan være så porøs at den flyter." },

  // Falske påstander / myter
  { statement: "Einstein strøk i matematikk som barn", isTrue: false,
    explanation: "Dette er en myte — Einstein var god i matematikk allerede som barn og mestret avanserte emner tidlig." },

  { statement: "Sukker gjør barn hyperaktive", isTrue: false,
    explanation: "Studier har ikke funnet en direkte sammenheng mellom sukkerinntak og varig hyperaktivitet hos barn." },

  { statement: "Bananer vokser på trær", isTrue: false,
    explanation: "Bananklassifiseres botanisk som en urt; planten er en stor urtaktig vekst, ikke et tre med klart ved." },

  { statement: "Stegosaurus og Tyrannosaurus rex levde samtidig", isTrue: false,
    explanation: "De levde i ulike perioder i mesozoikum — stegosaurus i juratiden, T. rex i krittiden, millioner av år fra hverandre." },

  { statement: "Man bruker enten 'venstre' eller 'høyre' hjernehalvdel mest", isTrue: false,
    explanation: "Selv om enkelte funksjoner er lateraliserte, samarbeider begge hjernehalvdeler i de fleste oppgaver." },

  { statement: "5G-nettet forårsaket COVID-19", isTrue: false,
    explanation: "Det finnes ingen vitenskapelig støtte for at mobilnettverk kan forårsake virussykdommer; COVID-19 er forårsaket av SARS-CoV-2-virus." },

    { statement: "Ketchup gjør at hår vokser raskere", isTrue: false,
    explanation: "Det finnes ingen vitenskapelig sammenheng mellom å smøre ketchup i hodebunnen og økt hårvekst." },

  { statement: "Humler kan ikke fly i motvind", isTrue: false,
    explanation: "Humler kan fly i ulike vindforhold; gamle beregninger som påsto de 'ikke kunne fly' var basert på feil eller forenklede modeller." },

  { statement: "Antibiotika er like effektive mot alle bakterier", isTrue: false,
    explanation: "Ulike antibiotika virker på ulike bakterier; resistensproblemer gjør også at ikke alle antibiotika virker mot alle bakterier." },

      // Sanne påstander
  { statement: "Tomater er en frukt", isTrue: true,
    explanation: "Botanisk regnes tomaten som en frukt (utviklet fra blomstens fruktknute), men mange regner den som en grønnsak." },

  { statement: "Noen frosker kan fryse helt og overleve", isTrue: true,
    explanation: "Arter som 'wood frog' kan tåle at kroppsvæsker delvis fryser ved å bruke sukkerarter som cryoprotektanter som beskytter cellene." },

  { statement: "En gruppe kråker kalles 'murder' på engelsk", isTrue: true,
    explanation: "Det engelske kollektive navnet for kråker er 'a murder of crows' — et gammelt, fargerikt uttrykk." },

  { statement: "Noen skilpadder kan 'puste' gjennom baken", isTrue: true,
    explanation: "Visse arter (f.eks. noen australske ferskvannsskilpadder) kan ta opp oksygen gjennom kloca (rompa)" },

  { statement: "Det finnes flere trær på jorden enn stjerner i Melkeveien", isTrue: true,
    explanation: "Man regner med at det finnes ~3 billioner (3000 milliarder) trær på jorden, mot 100-400 milliarder stjerner i Melkeveien — altså flere trær enn stjerner." },

  { statement: "Bananer er radioaktive", isTrue: true,
    explanation: "Bananer inneholder kalium, inkl. isotopen K-40, som gir et svært lavt nivå av naturlig radioaktivitet." },

  { statement: "Cleopatra levde nærmere månelandingen enn byggingen av de store pyramidene", isTrue: true,
    explanation: "Keopspyramiden ble bygd ~2560 f.Kr.; Cleopatra døde rundt 30 f.Kr.; månelandingen var 1969 e.Kr. Cleopatra er dermed nærmere i tid til 1969 enn til 2560 f.Kr." },

  { statement: "Venus roterer 'baklengs' sammenlignet med jorden", isTrue: true,
    explanation: "Venus har retrograd rotasjon — den snurrer i motsatt retning av de fleste planetene i solsystemet." },

  // Falske påstander / myter
  { statement: "Du svelger gjennomsnittlig åtte edderkopper i søvne hvert år", isTrue: false,
    explanation: "Dette er en populær myte uten bevis — edderkopper unngår vanligvis mennesker." },

  { statement: "Padder/frosker gir deg vorter", isTrue: false,
    explanation: "Menneskelige vorter skyldes virus (HPV). Berøring av padder/rovdyr gir ikke vorter." },

  { statement: "Lemminger begår masse­selvmord ved å hoppe i havet", isTrue: false,
    explanation: "Dette er en myte popularisert av feilaktige framstillinger; lemminger migrerer, men kaster seg ikke kollektivt i havet med vilje." },
];

function FactMyth() {
  const [showTask, setShowTask] = useState(false);
  const [currentStatement, setCurrentStatement] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [userAnswer, setUserAnswer] = useState(null);
  const [usedStatements, setUsedStatements] = useState([]);

  function getNewStatement() {
    const availableStatements = usedStatements.length >= FACT_MYTH_STATEMENTS.length
      ? FACT_MYTH_STATEMENTS
      : FACT_MYTH_STATEMENTS.filter(s => !usedStatements.find(used => used.statement === s.statement));

    const statement = availableStatements[randInt(0, availableStatements.length - 1)];
    setCurrentStatement(statement);
    setShowAnswer(false);
    setUserAnswer(null);
    setShowTask(true);

    if (usedStatements.length >= FACT_MYTH_STATEMENTS.length) {
      setUsedStatements([statement]);
    } else {
      setUsedStatements([...usedStatements, statement]);
    }
  }

  function handleAnswer(answer) {
    setUserAnswer(answer);
    setShowAnswer(true);
    
    if (answer === currentStatement.isTrue) {
      beep(300, 1200);
    } else {
      beep(400, 800);
    }
  }

  function resetActivity() {
    setShowTask(false);
    setCurrentStatement(null);
    setShowAnswer(false);
    setUserAnswer(null);
  }

  const buttonStyle = {
    padding: '16px 32px',
    borderRadius: '16px',
    fontWeight: 'bold',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
  };

  const isCorrect = userAnswer === currentStatement?.isTrue;

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{
        fontSize: '32px',
        fontWeight: 'bold',
        marginBottom: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px'
      }}>
        🤔 Fakta eller Myte
      </div>
      <p style={{
        color: '#cbd5e1',
        marginBottom: '24px',
        fontSize: '18px'
      }}>Er påstanden sann eller falsk? Test faktakunnskapen din!</p>
      
      {!showTask ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <p style={{ fontSize: '20px', color: '#cbd5e1' }}>Klar for en faktasjekk?</p>
          <button
            style={{
              ...buttonStyle,
              background: 'linear-gradient(45deg, #0891b2, #0c4a6e)'
            }}
            onClick={getNewStatement}
          >
            🎯 Vis påstand
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Påstand */}
          <div style={{
            background: 'linear-gradient(45deg, rgba(8, 145, 178, 0.2), rgba(12, 74, 110, 0.2))',
            border: '1px solid rgba(8, 145, 178, 0.3)',
            borderRadius: '24px',
            padding: '32px'
          }}>
            <div style={{
              fontSize: '24px',
              fontWeight: 'bold',
              marginBottom: '32px',
              lineHeight: '1.4',
              textAlign: 'center'
            }}>
              "{currentStatement?.statement}"
            </div>
            
            {/* Container med fast høyde for både knapper og fasit */}
            <div style={{ minHeight: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {!showAnswer ? (
                <div style={{ display: 'flex', gap: '24px', justifyContent: 'center' }}>
                  <button
                    style={{
                      ...buttonStyle,
                      background: 'linear-gradient(45deg, #22c55e, #16a34a)',
                      fontSize: '20px'
                    }}
                    onClick={() => handleAnswer(true)}
                  >
                    ✅ FAKTA
                  </button>
                  <button
                    style={{
                      ...buttonStyle,
                      background: 'linear-gradient(45deg, #ef4444, #dc2626)',
                      fontSize: '20px'
                    }}
                    onClick={() => handleAnswer(false)}
                  >
                    ❌ MYTE
                  </button>
                </div>
              ) : (
                <div style={{
                  padding: '24px',
                  background: isCorrect 
                    ? 'linear-gradient(45deg, rgba(34, 197, 94, 0.2), rgba(22, 163, 74, 0.2))'
                    : 'linear-gradient(45deg, rgba(239, 68, 68, 0.2), rgba(220, 38, 38, 0.2))',
                  border: isCorrect 
                    ? '1px solid rgba(34, 197, 94, 0.4)'
                    : '1px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: '16px',
                  width: '100%',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '20px',
                    fontWeight: 'bold',
                    marginBottom: '16px',
                    color: isCorrect ? '#22c55e' : '#ef4444'
                  }}>
                    {isCorrect ? '🎉 Riktig!' : '❌ Feil!'} 
                    {' '}Dette er {currentStatement?.isTrue ? 'FAKTA' : 'en MYTE'}
                  </div>
                  
                  <div style={{
                    fontSize: '16px',
                    lineHeight: '1.5',
                    color: '#e2e8f0',
                    textAlign: 'center'
                  }}>
                    💡 <strong>Forklaring:</strong> {currentStatement?.explanation}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Kontroller */}
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <button
              style={{
                ...buttonStyle,
                background: 'linear-gradient(45deg, #0891b2, #0c4a6e)'
              }}
              onClick={getNewStatement}
            >
              🔄 Ny påstand
            </button>
            
            <button
              style={{
                ...buttonStyle,
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.3)'
              }}
              onClick={resetActivity}
            >
              ⬅️ Tilbake
            </button>
          </div>

          {/* Tips */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '16px',
            padding: '16px',
            maxWidth: '480px',
            margin: '0 auto'
          }}>
            <div style={{ fontSize: '14px', color: '#cbd5e1' }}>
              💡 Tips: Del klassen i lag og la lagene diskutere før de svarer.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}